import { tokenService } from './auth/TokenService';
import { cacheService } from './CacheService';
import profileService from './ProfileService';
import { clientEnv } from '../buildTimeEnv';
// Note: userDataService import removed to break circular dependency
// Use lazy import via require() when needed

/**
 * @typedef {Object} SubscriptionAccess
 * @property {boolean} unlocked - Whether the content is unlocked for the user
 * @property {boolean} ads_enabled - Whether ads are enabled for this content
 * @property {Array<Object>} [childchannels] - Child channels with their own access settings
 * @property {string} childchannels[].dspro_id - Child channel DSPro ID
 * @property {boolean} childchannels[].unlocked - Whether child channel is unlocked
 * @property {boolean} childchannels[].ads_enabled - Whether ads are enabled for child channel
 */

/**
 * @typedef {Object} Channel
 * @property {string} _id - Unique channel identifier
 * @property {string} title - Channel title
 * @property {string} description - Channel description
 * @property {string} dspro_id - DSPro identifier
 * @property {boolean} is_product - Whether this is a product
 * @property {string} poster - Poster image URL
 * @property {string} programming_type - Type of programming (e.g., "Series")
 * @property {string} rating - Content rating (e.g., "TV-14", "TV-MA")
 * @property {string} slug - Channel slug for routing
 * @property {string} spotlight_poster - Spotlight poster image URL
 * @property {string} wallpaper - Wallpaper image URL
 * @property {SubscriptionAccess} subscription_access - Subscription and access information
 * @property {string} channel_type - Type of channel (e.g., "parent", "playlist")
 * @property {Array<Object>} [childchannels] - Child channels for parent channels
 * @property {string} childchannels[]._id - Child channel ID
 * @property {string} childchannels[].title - Child channel title
 * @property {string} childchannels[].description - Child channel description
 * @property {string} childchannels[].slug - Child channel slug
 * @property {string} childchannels[].poster - Child channel poster URL
 * @property {string} childchannels[].spotlight_poster - Child channel spotlight poster URL
 * @property {string} childchannels[].wallpaper - Child channel wallpaper URL
 * @property {string} childchannels[].dspro_id - Child channel DSPro ID
 * @property {string} childchannels[].video_id - Child channel video ID
 */

/**
 * @typedef {Object} Category
 * @property {string} _id - Unique category identifier
 * @property {string} display_type - Display type (e.g., "default")
 * @property {string} name - Category name
 * @property {string} slug - Category slug for routing
 * @property {string} type - Category type (e.g., "manual")
 */

/**
 * @typedef {Object} HomepageItem
 * @property {Category} category - Category information
 * @property {Array<Channel>} channels - Array of channels in this category
 */

/**
 * @typedef {Object} AppData
 * @property {string} api_url - Base API URL
 * @property {string} image_cdn_url - Image CDN URL
 * @property {Object} app_info - Application information object
 * @property {string} company_id - Company identifier
 * @property {string} token - Authentication token
 */

/**
 * @typedef {Object} WatchlistItem
 * @property {string} _id - Unique watchlist item identifier
 * @property {Array<string>} categories - Array of category IDs
 * @property {string} company_id - Company identifier
 * @property {string} description - Content description
 * @property {string} poster - Poster image URL
 * @property {string} programming_type - Type of programming (e.g., "Movie", "Series")
 * @property {string} slug - Content slug for routing
 * @property {string} spotlight_poster - Spotlight poster image URL
 * @property {string} title - Content title
 */

/**
 * @typedef {Object} UserData
 * @property {string} customer_id - Customer identifier
 * @property {string} token - User token
 * @property {Object} user_data - User-specific data object
 * @property {Array<WatchlistItem>} user_data.watchlist - User's watchlist items
 */

/**
 * @typedef {Object} AppLoadResponse
 * @property {Array<HomepageItem>} homepage - Array of homepage categories with their channels
 * @property {AppData} app_data - Application configuration data
 * @property {UserData} user_data - User-specific data including watchlist
 */

/**
 * @typedef {Object} EPGProgram
 * @property {string} program_id - Unique program identifier
 * @property {string} program_title - Program title
 * @property {string} program_language - Program language (e.g., "en")
 * @property {string} program_description - Program description
 * @property {number} duration - Program duration in seconds
 * @property {number} starts_at - Program start time as Unix timestamp
 * @property {number} ends_at - Program end time as Unix timestamp
 * @property {string} episode_title - Episode title (empty string if not applicable)
 * @property {number|null} episode - Episode number (null if not applicable)
 * @property {number|null} season - Season number (null if not applicable)
 * @property {number|null} part - Part number (null if not applicable)
 */

/**
 * @typedef {Object} EPGChannel
 * @property {string} _id - Unique channel identifier
 * @property {string} slug - Channel slug
 * @property {string} name - Channel name
 * @property {string} description - Channel description
 * @property {number|null} channel_number - Channel number (null if not applicable)
 * @property {string} logo - Channel logo URL
 * @property {string} poster - Channel poster URL
 * @property {string} spotlight_poster - Channel spotlight poster URL
 * @property {string} language - Channel language (e.g., "en")
 * @property {number} order_index - Channel display order
 * @property {Array<string>} category - Array of category names
 * @property {Array<string>} genres - Array of genre names
 * @property {string} rating - Content rating (empty string if not applicable)
 * @property {string} year - Year (empty string if not applicable)
 * @property {string} video_id - Video identifier
 * @property {Array<EPGProgram>} program - Array of programs for this channel
 * @property {SubscriptionAccess} subscription_access - Subscription and access information
 */

/**
 * @typedef {Object} EPGDataResponse
 * @property {Array<EPGChannel>} channels - Array of channels with their EPG data
 */

/**
 * @typedef {Object} PlatformConfig
 * @property {string} amazon_fire - Amazon Fire support ("true"/"false")
 * @property {string} andriod - Android support ("true"/"false")
 * @property {string} android_TV - Android TV support ("true"/"false")
 * @property {string} apple_tv - Apple TV support ("true"/"false")
 * @property {string} html5 - HTML5 support ("true"/"false")
 * @property {string} ios - iOS support ("true"/"false")
 * @property {string} roku - Roku support ("true"/"false")
 * @property {string} tizen - Tizen support ("true"/"false")
 * @property {string} vidaa - VIDAA support ("true"/"false")
 * @property {string} vizio - Vizio support ("true"/"false")
 * @property {string} webos - WebOS support ("true"/"false")
 * @property {string} website - Website support ("true"/"false")
 * @property {string} amagi_mrss - Amagi MRSS support ("true"/"false")
 */

/**
 * @typedef {Object} CategoryDetail
 * @property {string} _id - Unique category identifier
 * @property {string} company_id - Company identifier
 * @property {Array<Object>} custom_fields - Custom field configurations
 * @property {boolean} deleted - Whether category is deleted
 * @property {string} description - Category description
 * @property {string} display_name - Display name for the category
 * @property {string} display_type - Display type (e.g., "default")
 * @property {string} display_area - Display area configuration
 * @property {boolean} enabled - Whether category is enabled
 * @property {boolean} homepage - Whether category appears on homepage
 * @property {Object} image - Image dimensions
 * @property {number} image.width - Image width
 * @property {number} image.height - Image height
 * @property {boolean} menu - Whether category appears in menu
 * @property {boolean} movies_page - Whether category appears on movies page
 * @property {string} name - Category name
 * @property {Array<Object>} nested_categories - Nested categories
 * @property {Array<Object>} nested_categories_full - Full nested categories data
 * @property {string} notes - Category notes
 * @property {Array<PlatformConfig>} platforms - Platform support configuration
 * @property {string} poster - Category poster URL
 * @property {boolean} series_page - Whether category appears on series page
 * @property {string} slug - Category slug
 * @property {string} status - Category status (e.g., "published")
 * @property {Array<Object>} terms - Category terms
 * @property {string} type - Category type (e.g., "manual")
 * @property {number} weight - Category weight/priority
 * @property {string} wallpaper - Category wallpaper URL
 * @property {number} __v - Version number
 * @property {boolean} curators_page - Whether category appears on curators page
 * @property {boolean} discuss_page - Whether category appears on discuss page
 * @property {boolean} editorials_page - Whether category appears on editorials page
 * @property {boolean} partners_page - Whether category appears on partners page
 * @property {boolean} read_page - Whether category appears on read page
 * @property {boolean} themes_page - Whether category appears on themes page
 * @property {boolean} titles_page - Whether category appears on titles page
 * @property {boolean} watch_page - Whether category appears on watch page
 */

/**
 * @typedef {Object} VideoAds
 * @property {string} over - Overlay ads setting ("yes"/"no")
 * @property {string} pre - Pre-roll ads setting ("yes"/"no")
 * @property {string} post - Post-roll ads setting ("yes"/"no")
 * @property {string} mid - Mid-roll ads setting ("yes"/"no")
 * @property {Object} mid_offset - Mid-roll offset configuration
 * @property {string} mid_offset.mid_ads_number - Number of mid-roll ads
 * @property {Object} mid_offset.offsets - Mid-roll offset times
 * @property {Array<string>} mid_offset.offsets.linear - Linear offset times
 * @property {Object} offsets - General offset configuration
 * @property {Array<string>} offsets.linear - Linear offset times
 */

/**
 * @typedef {Object} Profile
 * @property {string} _id - Unique profile identifier
 * @property {string} profile_name - Display name for the profile
 * @property {string} profile_thumb - URL for the profile avatar
 */

/**
 * @typedef {Object} ProfilesResponse
 * @property {boolean} success - Whether the operation was successful
 * @property {Array<Profile>} profiles - Array of profile objects
 * @property {number} remaining - Number of additional profiles that can be created
 */

/**
 * @typedef {Object} ProfileTokenResponse
 * @property {boolean} success - Whether the operation was successful
 * @property {string} token - Profile token to be used in future requests
 */

/**
 * @typedef {Object} ProfileOperationResponse
 * @property {boolean} success - Whether the operation was successful
 * @property {boolean} profile_created - Whether the profile was successfully created (for create operations)
 * @property {boolean} profile_updated - Whether the profile was successfully updated (for update operations)
 * @property {boolean} profile_deleted - Whether the profile was successfully deleted (for delete operations)
 * @property {string} profile_thumb - URL for the new profile avatar (for regenerate operations)
 */

/**
 * @typedef {Object} CloseCaption
 * @property {string} srtPath - Subtitle file path
 * @property {string} srtLanguage - Subtitle language
 * @property {string} srtFileName - Subtitle file name
 * @property {string} uploaded - Upload timestamp
 * @property {string} srtExt - Subtitle file extension
 * @property {string} source - Source of subtitle
 * @property {string} originalFileName - Original file name
 */

/**
 * @typedef {Object} VideoPaths
 * @property {string} thumbsDir - Thumbnails directory path
 * @property {string} vodDir - VOD directory path
 * @property {string} source - Video source URL
 */

/**
 * @typedef {Object} RokuBifFiles
 * @property {string} sd - SD BIF file path
 * @property {string} hd - HD BIF file path
 * @property {string} fhd - FHD BIF file path
 */

/**
 * @typedef {Object} VideoDetail
 * @property {string} access - Video access level (e.g., "public")
 * @property {Array<string>} actors - Video actors
 * @property {VideoAds} ads - Video advertising configuration
 * @property {string} category - Video category
 * @property {Array<CloseCaption>} closeCaption - Closed captions
 * @property {string} company_id - Company identifier
 * @property {string} created_at - Creation timestamp
 * @property {Array<Object>} custom_fields - Custom fields
 * @property {string} description - Video description
 * @property {Array<string>} directors - Video directors
 * @property {string} duration - Video duration in seconds
 * @property {number|null} episode - Episode number
 * @property {number} frame_rate - Video frame rate
 * @property {Array<string>} genres - Video genres
 * @property {Array<string>} geoblock - Geographic blocking
 * @property {number} height - Video height
 * @property {string} language - Video language
 * @property {string} or_publish_date - Original publish date
 * @property {VideoPaths} paths - Video file paths
 * @property {string} poster - Video poster ID
 * @property {string} rating - Video rating
 * @property {Array<Object>} rating_reason - Rating reasons
 * @property {RokuBifFiles} roku_bif_files - Roku BIF files
 * @property {number|null} season - Season number
 * @property {string} short_description - Short description
 * @property {string} slug - Video slug
 * @property {string} status - Video status
 * @property {string} studio - Video studio
 * @property {Array<string>} tags - Video tags
 * @property {string} thumb - Video thumbnail ID
 * @property {Array<string>} thumbs - Video thumbnail IDs
 * @property {string} title - Video title
 * @property {string} wallpaper - Video wallpaper ID
 * @property {number} width - Video width
 * @property {Array<string>} writers - Video writers
 * @property {string} year - Video year
 * @property {string} _id - Video identifier
 */

/**
 * @typedef {Object} ThemePredefined
 * @property {boolean|string} ad_leader_header - Ad leader header setting
 * @property {boolean|string} ad_leader_footer - Ad leader footer setting
 * @property {boolean|string} product - Product setting
 * @property {boolean|string} menu - Menu setting
 * @property {boolean|string} user_info - User info setting
 * @property {boolean|string} subscribe_btn - Subscribe button setting
 */

/**
 * @typedef {Object} Theme
 * @property {ThemePredefined} predefined - Predefined theme settings
 */

/**
 * @typedef {Object} Weighting
 * @property {Object} weighting - Weighting configuration
 */

/**
 * @typedef {Object} BaseChannel
 * @property {string} _id - Unique channel identifier
 * @property {Array<string>} actors - Channel actors
 * @property {Array<CategoryDetail>} categories - Channel categories
 * @property {string} channel_type - Channel type (e.g., "single", "parent")
 * @property {string} channel_url - Channel URL
 * @property {string} company - Company name
 * @property {string} company_id - Company identifier
 * @property {Array<Object>} custom_fields - Custom fields
 * @property {string} description - Channel description
 * @property {Array<string>} directors - Channel directors
 * @property {string} dspro_id - DSPro identifier
 * @property {Array<string>} genres - Channel genres
 * @property {Array<string>} geo - Geographic availability
 * @property {boolean} has_youtube - Whether channel has YouTube content
 * @property {boolean} is_product - Whether channel is a product
 * @property {string} language - Channel language
 * @property {string} or_release_date - Original release date
 * @property {string} poster - Channel poster URL
 * @property {string} programming_type - Programming type (e.g., "Movie", "Series")
 * @property {string} rating - Content rating
 * @property {Array<Object>} rating_reason - Rating reasons
 * @property {number|null} season - Season number
 * @property {string} slug - Channel slug
 * @property {string} spotlight_company_id - Spotlight company identifier
 * @property {string} spotlight_poster - Spotlight poster URL
 * @property {Array<Object>} story_blocks - Story blocks
 * @property {Array<string>} tags - Channel tags
 * @property {Theme} theme - Channel theme configuration
 * @property {string} title - Channel title
 * @property {string} wallpaper - Channel wallpaper URL
 * @property {Array<string>} writers - Channel writers
 * @property {string} year - Channel year
 */

/**
 * @typedef {BaseChannel & Object} SingleChannel
 * @property {Array<VideoDetail>} [playlist] - Playlist configuration (optional)
 * @property {VideoDetail} video - Video details -- Will be first video in playlist if playlist is present
 */

/**
 * @typedef {BaseChannel & Object} ParentChannel
 * @property {Array<ChildChannel>} childchannels - Child channels
 * @property {Array<string>} childchannels_objectIds - Child channel object IDs
 */

/**
 * @typedef {BaseChannel & Object} ChildChannel
 * @property {string} category - Category
 * @property {Array<VideoDetail>} [playlist] - Playlist items
 * @property {string} videos_thumb - Videos thumbnail URL
 * @property {string} updated_at - Update timestamp
 * @property {string} step - Step status
 * @property {boolean} recommendable - Whether recommendable
 * @property {boolean} searchable - Whether searchable
 * @property {string} [playlist_id] - Playlist identifier
 * @property {string} [mrss_guid] - MRSS GUID
 * @property {string} [scheduled_publish_date] - Scheduled publish date
 * @property {number} owner_id - Owner identifier
 * @property {Object} [mrss_images_feed] - MRSS images feed
 * @property {string} videos_name - Videos name
 * @property {string} created_at - Creation timestamp
 * @property {string} user_flag - User flag status
 * @property {string} [playlist_type] - Playlist type
 * @property {VideoDetail} video - Video details
 */

/**
 * @typedef {Object} ChannelBySlugResponse
 * @property {Array<SingleChannel|ParentChannel>} channels - Array containing either a single channel or parent channel with children
 */

/**
 * @typedef {Object} PaginationInfo
 * @property {number} total - Total number of items
 * @property {number} limit - Number of items per page
 * @property {number} page - Current page number
 * @property {number} pages - Total number of pages
 */

/**
 * @typedef {Object} RelatedChannel
 * @property {string} _id - Unique channel identifier
 * @property {Array<string>} actors - Channel actors
 * @property {Array<string>} categories - Category IDs
 * @property {string} channel_type - Channel type (e.g., "single")
 * @property {string} company_id - Company identifier
 * @property {string} country - Country of origin
 * @property {Array<Object>} custom_fields - Custom fields
 * @property {Array<string>} directors - Channel directors
 * @property {string} display_title - Display title (empty string if not set)
 * @property {Array<string>} genres - Channel genres
 * @property {boolean} is_product - Whether channel is a product
 * @property {string} language - Channel language
 * @property {string} poster - Channel poster URL
 * @property {string} programming_type - Programming type (e.g., "Movie")
 * @property {string} rating - Content rating
 * @property {string} slug - Channel slug
 * @property {string} spotlight_poster - Spotlight poster URL
 * @property {string} title - Channel title
 * @property {string} wallpaper - Channel wallpaper URL
 * @property {Array<string>} writers - Channel writers
 * @property {string} year - Channel year
 */

/**
 * @typedef {Object} RelatedChannelsResponse
 * @property {Array<RelatedChannel>} channels - Array of related channels
 * @property {PaginationInfo} pages - Pagination information
 */

/**
 * @typedef {Object} CategoryImage
 * @property {number} width - Image width
 * @property {number} height - Image height
 * @property {string} poster - Category poster URL
 * @property {string} spotlight_poster - Category spotlight poster URL
 * @property {string} videos_thumb - Category videos thumbnail URL
 * @property {string} slug - Category slug
 */

/**
 * @typedef {Object} PageCategory
 * @property {string} _id - Unique category identifier
 * @property {string} company_id - Company identifier
 * @property {Array<Object>} custom_fields - Custom field configurations
 * @property {boolean} deleted - Whether category is deleted
 * @property {string} description - Category description
 * @property {string} display_name - Display name for the category
 * @property {string} display_type - Display type (e.g., "default")
 * @property {string} display_area - Display area configuration
 * @property {boolean} enabled - Whether category is enabled
 * @property {boolean} homepage - Whether category appears on homepage
 * @property {CategoryImage} image - Category image information
 * @property {boolean} menu - Whether category appears in menu
 * @property {boolean} movies_page - Whether category appears on movies page
 * @property {string} name - Category name
 * @property {Array<Object>} nested_categories - Nested categories
 * @property {Array<Object>} nested_categories_full - Full nested categories data
 * @property {string} notes - Category notes
 * @property {Array<PlatformConfig>} platforms - Platform support configuration
 * @property {string} poster - Category poster URL
 * @property {boolean} series_page - Whether category appears on series page
 * @property {string} slug - Category slug
 * @property {string} status - Category status (e.g., "published")
 * @property {Array<Object>} terms - Category terms
 * @property {string} type - Category type (e.g., "manual")
 * @property {number} weight - Category weight/priority
 * @property {string} wallpaper - Category wallpaper URL
 * @property {number} __v - Version number
 * @property {boolean} curators_page - Whether category appears on curators page
 * @property {boolean} discuss_page - Whether category appears on discuss page
 * @property {boolean} editorials_page - Whether category appears on editorials page
 * @property {boolean} partners_page - Whether category appears on partners page
 * @property {boolean} read_page - Whether category appears on read page
 * @property {boolean} themes_page - Whether category appears on themes page
 * @property {boolean} titles_page - Whether category appears on titles page
 * @property {boolean} watch_page - Whether category appears on watch page
 * @property {string} path - API path for category
 * @property {string} channels - API path for category channels
 */

/**
 * @typedef {Object} PageCategoriesResponse
 * @property {Array<PageCategory>} categories - Array of categories for the page
 */

/**
 * @typedef {Object} CategoryChannelsResponse
 * @property {Array<SingleChannel|ParentChannel>} channels - Array of channels in the category (same structure as getChannelBySlug)
 * @property {Array<CategoryDetail>} category - Array containing the category information
 */

/**
 * @typedef {Object} ResumptionItem
 * @property {string} _id - Unique identifier
 * @property {string} company_id - Company identifier
 * @property {string} user_id - User identifier
 * @property {string} title - Video title
 * @property {string} seriestitle - Series title (for series episodes)
 * @property {string} thumb - Thumbnail identifier
 * @property {string} [poster] - Poster identifier (optional)
 * @property {number} duration - Video duration in seconds
 * @property {number} point - Watch point in seconds
 * @property {number} progress - Progress percentage (0-1)
 * @property {number} episode - Episode number
 * @property {string} updatedAt - Last updated timestamp
 * @property {Object} access - Access control information
 * @property {boolean} access.unlocked - Whether content is unlocked
 * @property {boolean} access.ads_enabled - Whether ads are enabled
 * @property {Object} channel - Channel information
 * @property {string} channel._id - Channel ID
 * @property {string} channel.programming_type - Programming type (e.g., 'series')
 * @property {number} channel.season - Season number
 * @property {string} channel.slug - Channel slug
 * @property {string} channel.title - Channel title
 * @property {string} [channel.poster] - Channel poster URL (optional)
 * @property {string} [channel.spotlight_poster] - Channel spotlight poster URL (optional)
 * @property {Object} parent_channel - Parent channel information
 * @property {string} parent_channel._id - Parent channel ID
 * @property {string} parent_channel.title - Parent channel title
 * @property {string} parent_channel.slug - Parent channel slug
 * @property {string} parent_channel.programming_type - Parent channel programming type
 * @property {string} [parent_channel.poster] - Parent channel poster URL (optional)
 * @property {string} [parent_channel.spotlight_poster] - Parent channel spotlight poster URL (optional)
 */

/**
 * @typedef {Object} ResumptionsResponse
 * @property {boolean} success - Success status
 * @property {Object} data - Response data
 * @property {ResumptionItem[]} data['continue-watching'] - Continue watching items
 * @property {ResumptionItem[]} data['watch-again'] - Watch again items
 */

/**
 * @typedef {Object} WatchPointResponse
 * @property {boolean} success - Success status
 * @property {Object} data - Response data
 * @property {number|null} data.point - Watch point in seconds, or null if no data
 * @property {string} data.video_id - Video identifier
 */

/**
 * @typedef {Object} WatchPointSaveResponse
 * @property {boolean} success - Success status
 * @property {Object} data - Response data
 * @property {number} data.point - Saved watch point in seconds
 * @property {string} data.user_id - User identifier
 * @property {string} data.video_id - Video identifier
 * @property {string} data.company_id - Company identifier
 * @property {string} data.createdAt - Creation timestamp
 * @property {string} data.updatedAt - Last updated timestamp
 * @property {number} data.progress - Progress percentage (0-1)
 */

/**
 * @typedef {Object} WatchlistChannel
 * @property {string} _id - Channel identifier
 * @property {string} title - Channel title
 * @property {string} description - Channel description
 * @property {string} company_id - Company identifier
 * @property {string} poster - Channel poster URL
 * @property {string} spotlight_poster - Channel spotlight poster URL
 * @property {string} slug - Channel slug
 */

/**
 * @typedef {Object} WatchlistResponse
 * @property {boolean} success - Success status
 * @property {WatchlistChannel[]} channels - Array of watchlist channels
 */

/**
 * @typedef {Object} WatchlistChannelEntry
 * @property {string} channel_id - Channel ID
 * @property {string} parent_channel_id - Parent channel ID (optional)
 */

/**
 * @typedef {Object} WatchlistAddResult
 * @property {string} _id - Watchlist entry ID
 * @property {string} customer_id - Customer ID
 * @property {string} company_id - Company ID
 * @property {WatchlistChannelEntry[]} channels - Array of channel entries
 */

/**
 * @typedef {Object} WatchlistAddResponse
 * @property {boolean} success - Success status
 * @property {WatchlistAddResult[]} result - Array of watchlist results
 */

/**
 * @typedef {Object} WatchlistDeleteResponse
 * @property {boolean} success - Success status
 * @property {WatchlistAddResult[]} result - Array of remaining watchlist results
 */

/**
 * @typedef {Object} WatchlistSaveResponse
 * @property {boolean} success - Success status
 * @property {Object} result - Result data
 * @property {WatchlistChannel[]} result.channels - Updated watchlist channels
 */

/**
 * @typedef {Object} WatchlistAddOptions
 * @property {string} [channelId] - Channel ID to add
 * @property {string} [slug] - Channel slug to add (alternative to channelId)
 * @property {string} [parentChannelId] - Parent channel ID (optional)
 * @property {string} [parentChannelSlug] - Parent channel slug (optional, alternative to parentChannelId)
 */

/**
 * @typedef {Object} ResumptionsOptions
 * @property {string} [limit] - Limit of resumptions for each returned array (default: 100)
 * @property {string} [ct_min] - Minimum progress percentage for continue-watching (default: 0.05)
 * @property {string} [ct_max] - Maximum progress percentage for continue-watching (default: 0.95)
 * @property {string} [wa_min] - Minimum progress percentage for watch-again (default: 0.95)
 * @property {number} [most_recent] - Get only most recent values per channel (default: 0)
 * @property {number} [has_channel] - Limit to records with channel_id (default: 0)
 */

/**
 * @typedef {Object} WatchPointSaveOptions
 * @property {string} [channelId] - The ID of the channel this video is being viewed from
 * @property {string} [parentChannelId] - The ID of the parent channel (if applicable)
 */

/**
 * @typedef {Object} WatchlistOptions
 * @property {string} [exclude_live] - Exclude channels with programming type "Live Broadcast" (1 to exclude)
 * @property {string} [exclude_vod] - Include only channels with programming type "Live Broadcast" (1 to include only live)
 */

// Cache key for AppLoad data (must match AppLoadContext)
const appLoadCacheKey = 'SPOTLIGHT_GET_APP_LOAD';

const getProfileCacheKey = 'SPOTLIGHT_GET_PROFILES';

/**
 * SpotlightService - Handles API calls to the dotstudioPRO backend
 * Provides methods for user data, watchlists, resumptions, and video progress tracking
 */

class SpotlightService {
  constructor() {
    // Determine API base URL with fallbacks
    const isLocalDev =
      process.env.NODE_ENV === 'development' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    this.baseURL =
      (window.API_BASE_URL ||
        clientEnv.REACT_APP_SPOTLIGHT_API_URL ||
        'https://www.electricnow.tv/api') + '/';

    // Default cache TTL (time to live) in milliseconds
    this.defaultCacheTTL = 5 * 60 * 1000; // 5 minutes

    // Track in-flight requests to prevent duplicates
    this.pendingRequests = new Map();

    // Track in-flight token refresh requests to prevent concurrent calls
    this.pendingTokenRefresh = null;

    // Store current user in memory (not just cache)
    this.currentUser = null;

    // Store current client token in memory (not just cache)
    this.clientToken = null;

    // Safari detection
    this.isSafari = false;

    // Safari-specific circuit breaker for endless loops
    this.safariFailureCount = 0;
    this.safariMaxFailures = 5;
    this.safariCooldownUntil = 0;

    // Initialize service and ensure tokens are ready before any API calls
    this.initializationPromise = this.initializeService();
  }

  /**
   * Initialize the service and ensure tokens are ready
   * This method is called in the constructor and returns a promise
   * that resolves when the service is ready to make API calls
   */
  async initializeService() {
    console.log('🔧 SpotlightService: Initializing service...');
    
    // 1. Detect Safari
    this.isSafari = this.detectSafari();
    if (this.isSafari) {
      console.log('🦁 Safari detected - using Safari-specific initialization');
    }
    
    // 2. Load cached client token with retry for Safari
    if (this.isSafari) {
      await this.loadCachedClientTokenWithRetry();
    } else {
      this.loadCachedClientToken();
    }
    
    // 3. Set up token refresh interval (skip for Safari to avoid timing issues)
    if (!this.isSafari) {
      this.setupTokenRefreshInterval();
    } else {
      console.log('🦁 Safari: Skipping automatic token refresh interval');
    }
    
    // 4. Verify token is accessible
    const token = this.getClientTokenSync();
    if (token) {
      console.log('✅ SpotlightService: Initialization complete with client token');
    } else {
      console.warn('⚠️ SpotlightService: Initialization complete but no client token available (may be unauthenticated user)');
    }
    
    return true;
  }

  /**
   * Detect if the browser is Safari
   */
  detectSafari() {
    const userAgent = navigator.userAgent;
    const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
    return isSafari;
  }

  /**
   * Load cached client token with retry logic for Safari
   * Safari may have timing issues with localStorage access
   */
  async loadCachedClientTokenWithRetry(maxRetries = 3) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const cachedToken = cacheService.getClientToken();
        if (cachedToken && !cacheService.isClientTokenExpired()) {
          this.clientToken = cachedToken;
          console.log(`🦁 Safari: Loaded cached client token on attempt ${attempt}`);
          return true;
        } else if (attempt < maxRetries) {
          // Wait a bit before retrying (Safari may need time to sync localStorage)
          console.log(`🦁 Safari: No valid token on attempt ${attempt}, retrying...`);
          await new Promise(resolve => setTimeout(resolve, 100 * attempt));
        }
      } catch (error) {
        console.warn(`🦁 Safari: Failed to load cached client token on attempt ${attempt}:`, error);
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 100 * attempt));
        }
      }
    }
    
    console.log('🦁 Safari: No cached client token found after retries (may be first login)');
    return false;
  }

  /**
   * Set up 30-minute token refresh interval
   */
  setupTokenRefreshInterval() {
    // Clear any existing interval
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }

    // Set up new interval - refresh token every 30 minutes
    this.tokenRefreshInterval = setInterval(async () => {
      try {
        await this.devRefreshAuth();
      } catch (error) {
        console.warn('🔄 Scheduled token refresh failed:', error);
      }
    }, 30 * 60 * 1000); // 30 minutes in milliseconds
  }

  /**
   * Set the current user context for API calls
   * @param {Object} user - Auth0 user object
   */
  setCurrentUser(user) {
    if (user) {
      const previousUser = cacheService.get('USER');
      const isNewUser = !previousUser || previousUser.sub !== user.sub;
      
      cacheService.set('USER', user, 1440); // 24 hours
      
      // If this is a new user or we don't have a client token, set/refresh it
      const currentClientToken = cacheService.getClientToken();
      if (!currentClientToken || isNewUser) {
        console.log('SpotlightService: Setting client token for user:', isNewUser ? 'new user' : 'missing token');
        const token = user['https://www.electricnow.tv/dsp_customer_token'];
        
        // Debug: Log whether the token claim exists in the Auth0 user object
        if (!token) {
          console.error('❌ SpotlightService: Auth0 user object is MISSING the client token claim!');
          console.error('   Expected claim: https://www.electricnow.tv/dsp_customer_token');
          console.error('   Available claims:', Object.keys(user).filter(k => k.startsWith('https://')));
          console.error('   User email:', user.email);
          console.error('   This is likely an Auth0 Action/Rule configuration issue.');
        } else {
          console.log('✅ SpotlightService: Found client token in Auth0 user, length:', token.length);
        }
        
        this.setClientToken(token);
      }
    } else {
      cacheService.remove('USER');
    }
  }

  /**
   * Load cached client token on startup
   */
  loadCachedClientToken() {
    try {
      const cachedToken = cacheService.getClientToken();
      if (cachedToken && !cacheService.isClientTokenExpired()) {
        this.clientToken = cachedToken;
        console.log('SpotlightService: Loaded cached client token on startup');
      }
    } catch (error) {
      console.warn('SpotlightService: Failed to load cached client token:', error);
    }
  }

  /**
   * Set the client token for API calls
   * @param {string} clientToken - Client token
   */
  setClientToken(clientToken) {
    if (clientToken) {
      // Check if this is a different token than what we have
      const previousToken = this.clientToken;
      const isNewToken = previousToken !== clientToken;
      
      this.clientToken = clientToken;
      cacheService.setClientToken(clientToken);
      
      // If we're setting a new/different token, clear subscription caches
      // because the new token may have different entitlements
      if (isNewToken && previousToken) {
        console.log('🔑 SpotlightService: New client token detected, clearing subscription caches...');
        this.clearSubscriptionCaches();
      }
    }
  }

  /**
   * Get the current user context
   * @returns {Object|null} Current user object or null
   */
  getCurrentUser() {
    return cacheService.get('USER');
  }

  /**
   * Clear the current user context
   */
  clearCurrentUser() {
    cacheService.remove('USER');
  }

  /**
   * Get client token, refreshing if necessary
   * @returns {Promise<string|null>} Client token or null if not available
   */
  async getClientToken() {
    // Check if we have a valid cached token
    const cachedToken = cacheService.getClientToken();
    const isExpired = cacheService.isClientTokenExpired();

    // If we have a token but it's expired, we need to refresh it
    if (cachedToken && isExpired) {
      // Token is expired, will refresh
    } else if (cachedToken && !isExpired) {
      return cachedToken;
    }

    // Get user from cache
    const user = this.getCurrentUser();
    if (!user || !user.email) {
      // Don't trigger logout if there's simply no user (this is normal for unauthenticated users)
      // Only trigger logout if we have a user but can't get a token
      return null;
    }

    // Check if there's already a token refresh in progress
    if (this.pendingTokenRefresh) {
      return this.pendingTokenRefresh;
    }

    // Create the token refresh promise
    const tokenRefreshPromise = (async () => {
      try {
        let clientToken = null;

        // If we have an existing token (even if expired), try to refresh it
        if (cachedToken) {
          clientToken = await this.refreshClientToken();
        }

        // If refresh failed or no existing token, get it from Auth0 user data
        if (!clientToken) {
          clientToken = user['https://www.electricnow.tv/dsp_customer_token'];

          if (clientToken) {
            // Debug: Check token format
            console.log('🔍 Auth0 client token format check:', {
              type: typeof clientToken,
              length: clientToken?.length,
              startsWithEyJ: clientToken?.startsWith('eyJ'),
              isString: typeof clientToken === 'string',
              preview: clientToken?.substring(0, 50) + '...'
            });
            this.setClientToken(clientToken);
          }
        }

        if (clientToken) {
          return clientToken;
        }

        // If we still don't have a token after trying to get it from Auth0, trigger logout
        // Only do this if we actually have a user (not just unauthenticated)
        this.handleLogout();
      } catch (error) {
        console.error('SpotlightService: Error getting client token:', error);
        // If there's an error getting the token, trigger logout
        this.handleLogout();
      }

      return null;
    })();

    // Store the promise for concurrent requests
    this.pendingTokenRefresh = tokenRefreshPromise;

    try {
      const result = await tokenRefreshPromise;
      return result;
    } finally {
      // Clean up the pending request
      this.pendingTokenRefresh = null;
    }
  }


  /**
   * Handle logout when authentication fails
   */
  handleLogout() {
    // SpotlightService: Handling logout due to authentication failure

    // Clear all cached data
    cacheService.clearAllCriticalData();

    // Clear current user and tokens
    this.clearCurrentUser();
    this.clearClientToken();
    this.clearCurrentProfileToken();

    // Clear profile service data
    if (typeof profileService !== 'undefined') {
      profileService.clearAllData();
    }

    // Clear user data service (lazy import to avoid circular dependency)
    try {
      const { userDataService } = require('./UserDataService');
      if (userDataService) {
        userDataService.clearCurrentUser();
      }
    } catch (e) {
      // UserDataService may not be available during initialization
    }

    // Trigger a custom event that the app can listen to for proper logout handling
    if (typeof window !== 'undefined') {
      const logoutEvent = new CustomEvent('spotlightServiceLogout', {
        detail: { reason: 'authentication_failure' },
      });
      window.dispatchEvent(logoutEvent);
    }
  }

  /**
   * Clear the cached client token
   */
  clearClientToken() {
    this.clientToken = null;
    cacheService.remove('CLIENT_TOKEN');
    cacheService.remove('CLIENT_TOKEN_EXPIRY');
  }

  /**
   * Check if we have a valid client token
   * @returns {boolean} True if we have a valid token
   */
  hasValidToken() {
    const cachedToken = cacheService.getClientToken();
    return !!cachedToken;
  }

  /**
   * Get the current client token synchronously (without refresh)
   * @returns {string|null} Current client token or null
   */
  getClientTokenSync() {
    return this.clientToken || cacheService.getClientToken();
  }

  /**
   * Refresh client token for authenticated users
   * @returns {Promise<string|null>} Refreshed client token or null if not available
   */
  async refreshClientToken() {
    // Get the API access token from cache directly to avoid infinite loop
    const clientToken = cacheService.getClientToken();

    try {
      if (!clientToken) throw new Error('User has no client token.');
      
      // Debug: Check what token we're trying to refresh
      console.log('🔍 Refresh token debug:', {
        hasToken: !!clientToken,
        tokenType: typeof clientToken,
        tokenLength: clientToken?.length,
        startsWithEyJ: clientToken?.startsWith('eyJ'),
        preview: clientToken?.substring(0, 50) + '...'
      });
      
      // Use direct fetch instead of callAPI to avoid infinite loop
      const authHeaders = await tokenService.getAuthHeaders();
      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
          ...(clientToken ? { 'x-client-token': clientToken } : {}),
        },
      };

      const response = await fetch(`${this.baseURL}users/token/refresh`, fetchOptions);
      if (!response.ok) {
        console.warn(
          'SpotlightService: Client token refresh failed - HTTP error:',
          response.status,
          response.statusText,
        );

        // Try to get error details
        try {
          const errorText = await response.text();
          console.warn('SpotlightService: Refresh error response:', errorText);
          
          // If the error indicates the client token is invalid/corrupted, clear it
          if (errorText.includes('Invalid client token') || errorText.includes('could not decode')) {
            console.log('SpotlightService: Clearing corrupted client token');
            this.clearClientToken();
          }
        } catch (e) {
          console.warn('SpotlightService: Could not read error response');
        }

        return null;
      }

      const responseData = await response.json();

      if (responseData.success && responseData.client_token) {
        // Check if the new token is actually different
        const isSameToken = clientToken === responseData.client_token;
        if (isSameToken) {
          console.warn(
            '🔄 refreshClientToken - WARNING: Server returned the same token! This might indicate a server-side issue.',
          );
        }

        // Update the internal client token with the new token
        this.setClientToken(responseData.client_token);

        // Verify the token was stored by reading it back from cache
        const storedToken = cacheService.getClientToken();
        const tokensMatch = responseData.client_token === storedToken;
        if (tokensMatch) {
          // SpotlightService: Client token refreshed successfully
          return storedToken;
        }
      } else {
        console.warn(
          'SpotlightService: Client token refresh failed - invalid response',
          responseData,
        );
      }
    } catch (error) {
      console.error('SpotlightService: Error refreshing client token:', error);
    }

    return null;
  }

  /**
   * Ensure we have a valid client token, creating a fresh one if needed
   * This is useful when the current token is corrupted or missing
   */
  async ensureClientToken() {
    try {
      // If we already have a valid client token, return it
      if (this.clientToken) {
        return this.clientToken;
      }

      
      // Clear any existing corrupted token
      this.clearClientToken();

      // Get the client token from Auth0 user data (same as initial setup)
      const user = this.getCurrentUser();
      if (user && user['https://www.electricnow.tv/dsp_customer_token']) {
        const freshToken = user['https://www.electricnow.tv/dsp_customer_token'];
        
        
        this.setClientToken(freshToken);
        return this.clientToken;
      } else {
        throw new Error('No client token available in user data');
      }
    } catch (error) {
      console.error('SpotlightService: Error ensuring client token:', error);
      throw error;
    }
  }

  /**
   * Centralized fetch method that handles:
   * - AppLoad readiness check
   * - Auth token injection
   * - Client token injection (when user is available)
   * - Caching
   * - Request deduplication
   *
   * @param {string} path - The URL to fetch
   * @param {string} cacheKey - Key for caching/deduplication
   * @param {Object} [options] - Fetch options
   * @param {string} [options.method='GET'] - HTTP method
   * @param {Object} [options.body] - Request body for POST/PUT
   * @param {Object} [options.headers] - Additional headers
   * @param {number} [options.cacheTTL] - Cache TTL in ms
   * @param {boolean} [options.requiresClientAuth=false] - Whether this request needs client auth headers
   * @param {boolean} [options.skipCache=false] - Whether this request should ignore the cache
   * @returns {Promise<any>} Parsed response data
   */
  async callAPI(path, cacheKey, options = {}) {
    // CRITICAL: Wait for service initialization to complete before making any API calls
    // This ensures tokens are loaded and ready, especially important for Safari
    await this.initializationPromise;

    const {
      method = 'GET',
      body,
      headers = {},
      cacheTTL = this.defaultCacheTTL,
      requiresClientAuth = false,
      skipCache = false,
    } = options;

    // Token validation for authenticated endpoints
    if (requiresClientAuth) {
      const clientToken = this.getClientTokenSync();
      const user = this.getCurrentUser();
      
      if (!clientToken && user) {
        // User is authenticated but no client token available
        console.error('🚨 Token Validation Failed:', {
          path,
          hasUser: !!user,
          hasClientToken: !!clientToken,
          userEmail: user?.email,
          isSafari: this.isSafari
        });
        
        if (this.isSafari) {
          console.error('🦁 Safari: This may be due to localStorage quota issues. Tokens may have failed to save.');
        }
        
        throw new Error(`Cannot make authenticated request to ${path}: client token missing despite authenticated user`);
      }
    }

    // Safari circuit breaker - prevent endless loops
    const userAgent = navigator.userAgent;
    const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
    if (isSafari) {
      const now = Date.now();
      
      // Check if we're in cooldown period
      if (now < this.safariCooldownUntil) {
        const remainingCooldown = Math.ceil((this.safariCooldownUntil - now) / 1000);
        console.warn(`🦎 Safari: In cooldown period, ${remainingCooldown}s remaining. Blocking request to:`, path);
        throw new Error(`Safari is in cooldown mode due to repeated failures. Please wait ${remainingCooldown} seconds or test on staging/production.`);
      }
    }

    // Track if we've already attempted token refresh for this request
    let hasAttemptedAccessTokenRefresh = false;
    let hasAttemptedClientTokenRefresh = false;

    // Check cache first (only if cacheKey is provided and not skipping cache)
    if (cacheKey && !skipCache) {
      const cachedData = cacheService.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      // Check for in-flight request immediately
      if (this.pendingRequests.has(cacheKey)) {
        return this.pendingRequests.get(cacheKey);
      }
    }

    // Create the promise before any async operations
    const requestPromise = (async () => {
      try {

        // For non-appload requests, ensure appload data is ready
        if (!path.includes('appload') && !path.includes('auth/refresh')) {
          // Wait for any existing appload request
          const appLoadData = cacheService.get(appLoadCacheKey);
          if (!appLoadData) {
            // If no appload data and no pending request, trigger appload
            if (!this.pendingRequests.has(appLoadCacheKey)) {
              await this.getAppLoad();
            } else {
              // Wait for pending appload request
              await this.pendingRequests.get(appLoadCacheKey);
            }
          }
        }

        // Prepare request with auth if needed
        const fetchOptions = {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
        };

        // Safari-specific request modifications to bypass CloudFront filtering
        const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
        if (isSafari) {
          // Add headers that might help Safari requests pass through CloudFront
          fetchOptions.headers = {
            ...fetchOptions.headers,
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json, text/plain, */*',
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            // Additional headers to make Safari requests more Chrome-like
            'Sec-Fetch-Dest': 'empty',
            'Sec-Fetch-Mode': 'cors',
            'Sec-Fetch-Site': 'cross-site',
            // Try to make Safari requests look more like Chrome requests
            'User-Agent': navigator.userAgent.replace(/Version\/[\d.]+\s+/, '').replace(/Safari\/[\d.]+/, 'Chrome/120.0.0.0 Safari/537.36'),
          };
          
          // Try different request modes for Safari
          fetchOptions.mode = 'cors';
          fetchOptions.credentials = 'same-origin';
          
          console.log('🦎 Safari: Modified request headers for CloudFront compatibility:', {
            path,
            headers: Object.keys(fetchOptions.headers),
            mode: fetchOptions.mode,
            credentials: fetchOptions.credentials
          });
        }

        if (body) {
          fetchOptions.body = typeof body === 'string' ? body : JSON.stringify(body);
        }

        const authHeaders = await tokenService.getAuthHeaders();
        fetchOptions.headers = {
          ...fetchOptions.headers,
          ...authHeaders,
        };


        if (requiresClientAuth) {
          // Add client token if user is available
          const clientToken = await this.getClientToken();

          if (clientToken) {
            fetchOptions.headers['x-client-token'] = clientToken;
            if (path.includes('reactions/by-channel')) {
              console.log('SpotlightService: Added x-client-token for reactions endpoint, length:', clientToken.length);
            }
          } else {
            if (path.includes('reactions/by-channel')) {
              console.warn('SpotlightService: No client token available for reactions endpoint');
            }
          }

          // Add profile token if available
          const currentProfile = profileService.getCurrentProfile();
          if (currentProfile?._id) {
            const cachedProfileToken = cacheService.getProfileToken(currentProfile._id);
            const isProfileTokenExpired = cacheService.isProfileTokenExpired(currentProfile._id);

            if (cachedProfileToken && !isProfileTokenExpired) {
              // Use cached profile token
              fetchOptions.headers['x-profile-token'] = cachedProfileToken;
              if (path.includes('reactions/by-channel')) {
                console.log('SpotlightService: Added x-profile-token (cached) for reactions endpoint, length:', cachedProfileToken.length);
              }
            } else if (cachedProfileToken && isProfileTokenExpired) {
              // Profile token is expired, refresh it BEFORE making the request
              try {
                const refreshedProfileToken = await this.refreshProfileToken(currentProfile._id);
                if (refreshedProfileToken) {
                  fetchOptions.headers['x-profile-token'] = refreshedProfileToken;
                  if (path.includes('reactions/by-channel')) {
                    console.log('SpotlightService: Added x-profile-token (refreshed) for reactions endpoint, length:', refreshedProfileToken.length);
                  }
                }
              } catch (profileTokenError) {
                console.warn(
                  'SpotlightService: Failed to refresh expired profile token:',
                  profileTokenError,
                );
              }
            } else if (!cachedProfileToken) {
              // No cached token, get a fresh one
              try {
                const freshProfileToken = await this.getProfileTokenWithCache(currentProfile._id);
                if (freshProfileToken) {
                  fetchOptions.headers['x-profile-token'] = freshProfileToken;
                  if (path.includes('reactions/by-channel')) {
                    console.log('SpotlightService: Added x-profile-token (fresh) for reactions endpoint, length:', freshProfileToken.length);
                  }
                }
              } catch (profileTokenError) {
                console.warn(
                  'SpotlightService: Failed to get fresh profile token:',
                  profileTokenError,
                );
              }
            }
          }
        }


        // Make the request with Safari fallback strategies
        let response;
        try {
          response = await fetch(`${this.baseURL}${path}`, fetchOptions);
        } catch (networkError) {
          // Safari fallback: Try with minimal headers if the first request fails
          if (isSafari && (networkError.message.includes('Load failed') || networkError.message.includes('Failed to fetch'))) {
            console.log('🦎 Safari: First request failed, trying with minimal headers...');
            try {
              const authHeaders = await tokenService.getAuthHeaders();
              const minimalOptions = {
                method,
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
                  ...authHeaders,
                },
                mode: 'cors',
                credentials: 'omit',
              };

              if (body) {
                minimalOptions.body = typeof body === 'string' ? body : JSON.stringify(body);
              }

              response = await fetch(`${this.baseURL}${path}`, minimalOptions);
              console.log('🦎 Safari: Minimal headers request succeeded!');
            } catch (fallbackError) {
              console.log('🦎 Safari: Minimal headers request also failed, trying no-cors mode...');
              // Last resort: try no-cors mode (limited response access but might work)
              try {
                const authHeaders = await tokenService.getAuthHeaders();
                const noCorsOptions = {
                  method,
                  headers: {
                    ...authHeaders,
                  },
                  mode: 'no-cors',
                };

                if (body && method !== 'GET') {
                  noCorsOptions.body = typeof body === 'string' ? body : JSON.stringify(body);
                }

                response = await fetch(`${this.baseURL}${path}`, noCorsOptions);
                console.log('🦎 Safari: No-cors request completed (limited response access)');
              } catch (noCorsError) {
                console.log('🦎 Safari: All fallback attempts failed, proceeding with original error');
                throw networkError; // Throw original error
              }
            }
          } else {
            throw networkError;
          }
        }
        
        // Continue with original error handling if we still have network issues
        if (!response) {
          throw new Error('No response received after all retry attempts');
        }

        // Handle 403 Forbidden - Check for expired tokens FIRST before assuming CloudFront issue
        // Client and profile tokens expire every 8 days, so 403 often means token refresh needed
        if (response.status === 403 && requiresClientAuth && !hasAttemptedClientTokenRefresh) {
          console.log('🔄 SpotlightService: Got 403 on client auth request, attempting token refresh (tokens expire every 8 days):', path);
          hasAttemptedClientTokenRefresh = true;

          // Force a token refresh instead of using getClientToken which returns cached tokens
          let refreshedToken = await this.refreshClientToken();
          if (refreshedToken) {
            // Store the refreshed token centrally for future requests
            this.setClientToken(refreshedToken);

            // Update the client token in the request headers
            fetchOptions.headers['x-client-token'] = refreshedToken;

            // If we have a current profile token, refresh it as well since client token was refreshed
            // Profile tokens also expire every 8 days
            const currentProfile = profileService.getCurrentProfile();
            if (currentProfile?._id) {
              try {
                const refreshedProfileToken = await this.refreshProfileToken(currentProfile._id);
                if (refreshedProfileToken) {
                  // Update the profile token in the request headers
                  fetchOptions.headers['x-profile-token'] = refreshedProfileToken;
                }
              } catch (profileTokenError) {
                console.warn(
                  'SpotlightService: Failed to refresh profile token after client token refresh:',
                  profileTokenError,
                );
              }
            }

            // Retry the request with the new tokens
            console.log('🔄 SpotlightService: Retrying request after token refresh for:', path);
            response = await fetch(`${this.baseURL}${path}`, fetchOptions);

            if (response.ok) {
              console.log('✅ SpotlightService: Request succeeded after token refresh for:', path);
              // Continue processing the successful response below
            } else if (response.status === 403) {
              // Still getting 403 after token refresh - tokens are likely invalid
              // Log out to force fresh authentication
              console.error('🔄 SpotlightService: Still getting 403 after token refresh. Tokens are invalid. Logging out to reset authentication.');
              this.handleLogout();
              throw new Error('Authentication tokens are invalid. Please log in again.');
            } else {
              // Different error after refresh, throw it
              throw new Error(
                `HTTP error! status: ${response.status} (after client token refresh)`,
              );
            }
          } else {
            // Token refresh failed - tokens are likely invalid or expired beyond refresh
            // Log user out to force fresh authentication and token generation
            console.error('🔄 SpotlightService: Client token refresh failed - tokens are invalid. Logging out to reset authentication.');
            this.handleLogout();
            throw new Error('Authentication tokens are invalid. Please log in again.');
          }
        }

        // Handle 403 Forbidden from CloudFront/CDN (Safari-specific issue)
        // Only check this if we've already tried token refresh or it's not a client auth request
        if (response.status === 403) {
          const responseHeaders = Object.fromEntries(response.headers.entries());
          const isCloudFrontError = responseHeaders['via']?.includes('cloudfront') || 
                                   responseHeaders['x-cache']?.includes('cloudfront') ||
                                   responseHeaders['server'] === 'AmazonS3';
          
          if (isCloudFrontError) {
            console.error('🌩️ CloudFront 403 Error detected:', {
              path,
              headers: responseHeaders,
              userAgent: navigator.userAgent,
              isSafari: navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome'),
              requiresClientAuth,
              hasAttemptedClientTokenRefresh
            });
            
            // For Safari CloudFront issues, provide specific guidance
            const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
            if (isSafari) {
              this.safariFailureCount++;
              console.log(`🦎 Safari: CloudFront 403 failure #${this.safariFailureCount}/${this.safariMaxFailures}`);
              
              if (this.safariFailureCount >= this.safariMaxFailures) {
                this.safariCooldownUntil = Date.now() + 60000; // 60 seconds
                console.error(`🦎 Safari: Too many CloudFront failures. Entering cooldown.`);
                console.error('🦎 Safari: This appears to be a CDN configuration issue. Please contact support or try Chrome.');
              }
            }
            
            throw new Error(`CloudFront blocked the request (403). This may be a CDN configuration issue affecting Safari. Please try Chrome or contact support.`);
          }
        }

        // Handle 401 Unauthorized or 400 Bad Request (expired/invalid token)
        if (
          (response.status === 401 || response.status === 400) &&
          !hasAttemptedAccessTokenRefresh
        ) {
          console.log('🔄 SpotlightService: Got 401/400, attempting token refresh for:', path);
          hasAttemptedAccessTokenRefresh = true;

          // Try to refresh the token using the proxy's auth/refresh endpoint
          try {
            const currentToken = await tokenService.getToken();
            console.log('🔄 SpotlightService: Current token for refresh:', currentToken ? '[REDACTED]' : 'null');

            console.log('🔄 SpotlightService: Calling token refresh endpoint...');
            const refreshResponse = await fetch(`${this.baseURL}auth/refresh`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                token: currentToken,
              }),
            });
            
            console.log('🔄 SpotlightService: Token refresh response status:', refreshResponse.status);
            
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              console.log('🔄 SpotlightService: Token refresh data received:', refreshData ? 'Success' : 'Empty');
              
              if (refreshData && refreshData.token) {
                const refreshedToken = refreshData.token;

                // Store the refreshed token in the primary access token cache
                // This is the ONLY place we need to update - TokenService will use this
                tokenService.storeToken(refreshedToken);
                console.log('🔄 SpotlightService: Stored refreshed token, length:', refreshedToken.length);

                // For profile-related requests, also refresh the client token
                // since profile auth requires both tokens to be valid
                if (
                  path.includes('profile') &&
                  requiresClientAuth &&
                  !hasAttemptedClientTokenRefresh
                ) {
                  hasAttemptedClientTokenRefresh = true;

                  const refreshedClientToken = await this.refreshClientToken();
                  if (refreshedClientToken) {
                    // Update the client token in the request headers
                    fetchOptions.headers['x-client-token'] = refreshedClientToken;
                  }
                }

                // Regenerate auth headers with the new token
                const newAuthHeaders = await tokenService.getAuthHeaders();
                fetchOptions.headers = {
                  ...fetchOptions.headers,
                  ...newAuthHeaders,
                };

                // Retry the request with the fresh token
                console.log('🔄 SpotlightService: Retrying request after token refresh for:', path);
                console.log('🔄 SpotlightService: Retry headers contain x-access-token:', !!fetchOptions.headers['x-access-token']);
                response = await fetch(`${this.baseURL}${path}`, fetchOptions);

                if (!response.ok) {
                  // If we still get an error after token refresh, tokens are likely invalid
                  // Log user out to force fresh authentication
                  if (response.status === 401 || response.status === 403) {
                    console.error('🔄 SpotlightService: Request still failed after token refresh with 401/403. Logging out to reset authentication.');
                    this.handleLogout();
                    throw new Error('Authentication tokens are invalid. Please log in again.');
                  }
                  // If we still get an error after token refresh, don't retry again
                  console.error('🔄 SpotlightService: Request still failed after token refresh:', response.status, path);
                  throw new Error(`HTTP error! status: ${response.status} (after token refresh)`);
                } else {
                  console.log('✅ SpotlightService: Request succeeded after token refresh for:', path);
                  
                  // Safari: Reset failure count on successful request
                  const userAgent = navigator.userAgent;
                  const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
                  if (isSafari && this.safariFailureCount > 0) {
                    console.log('🦎 Safari: Resetting failure count after successful request');
                    this.safariFailureCount = 0;
                  }
                }
              } else {
                throw new Error('Invalid response from token refresh endpoint');
              }
            } else {
              console.error('🔄 SpotlightService: Token refresh failed with status:', refreshResponse.status);
              
              // Try to get the error response body for more details
              try {
                const errorText = await refreshResponse.text();
                console.error('🔄 SpotlightService: Token refresh error body:', errorText);
              } catch (e) {
                console.error('🔄 SpotlightService: Could not read error response body');
              }
              
              // If token refresh fails with 401/403, tokens are invalid - log out
              if (refreshResponse.status === 401 || refreshResponse.status === 403) {
                console.error('🔄 SpotlightService: Token refresh failed with 401/403. Logging out to reset authentication.');
                this.handleLogout();
                throw new Error('Authentication tokens are invalid. Please log in again.');
              }
              
              throw new Error(`Token refresh failed with status: ${refreshResponse.status}`);
            }
          } catch (tokenError) {
            console.error('SpotlightService: Token refresh failed:', tokenError);
            
            // If the error message indicates invalid tokens, log out
            if (tokenError.message && (tokenError.message.includes('invalid') || tokenError.message.includes('expired'))) {
              console.error('🔄 SpotlightService: Token refresh error indicates invalid tokens. Logging out to reset authentication.');
              this.handleLogout();
              throw new Error('Authentication tokens are invalid. Please log in again.');
            }
            
            // Safari-specific: Track failures and implement circuit breaker
            const userAgent = navigator.userAgent;
            const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
            console.log('🦎 Safari Detection:', {
              userAgent: userAgent,
              isSafari: isSafari,
              failureCount: this.safariFailureCount,
              includes_safari: userAgent.includes('Safari'),
              includes_chrome: userAgent.includes('Chrome'),
              environment: window.location.hostname,
              baseURL: this.baseURL
            });
            
            if (isSafari) {
              this.safariFailureCount++;
              console.log(`🦎 Safari: Token refresh failure #${this.safariFailureCount}/${this.safariMaxFailures}`);
              
              if (this.safariFailureCount >= this.safariMaxFailures) {
                // Trigger 30-second cooldown
                this.safariCooldownUntil = Date.now() + 30000; // 30 seconds
                console.error(`🦎 Safari: Too many failures (${this.safariFailureCount}). Entering 30-second cooldown.`);
                console.error('🦎 Safari: Please test on staging/production or use Chrome for development.');
              } else {
                // Add delay for remaining attempts
                console.log('🦎 Safari: Adding delay after token refresh failure to prevent rate limiting');
                await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay
              }
            }
            throw new Error(`HTTP error! status: ${response.status} (token refresh failed)`);
          }
        }
        // Handle 4xx responses with "Invalid client token" error
        if (
          response.status >= 400 &&
          response.status < 500 &&
          requiresClientAuth &&
          !hasAttemptedClientTokenRefresh
        ) {
          // Parse the response to check for "Invalid client token" error
          try {
            const errorData = await response.clone().json();
            const errorMessage = errorData?.error || errorData;
            if (errorMessage?.includes('Invalid client token')) {
              hasAttemptedClientTokenRefresh = true;
              // Force a token refresh instead of using getClientToken which returns cached tokens
              let refreshedToken = await this.refreshClientToken();

              if (refreshedToken) {
                // Store the refreshed token centrally for future requests
                this.setClientToken(refreshedToken);

                // Update the client token in the request headers
                fetchOptions.headers['x-client-token'] = refreshedToken;

                // If we have a current profile token, refresh it as well since client token was refreshed
                const currentProfile = profileService.getCurrentProfile();
                if (currentProfile?._id) {
                  try {
                    const refreshedProfileToken = await this.refreshProfileToken(
                      currentProfile._id,
                    );
                    if (refreshedProfileToken) {
                      // Update the profile token in the request headers
                      fetchOptions.headers['x-profile-token'] = refreshedProfileToken;
                    }
                  } catch (profileTokenError) {
                    console.warn(
                      'SpotlightService: Failed to refresh profile token after client token refresh:',
                      profileTokenError,
                    );
                  }
                }

                // Retry the request with the new token
                response = await fetch(`${this.baseURL}${path}`, fetchOptions);

                if (!response.ok) {
                  // If we still get an error after token refresh, don't retry again
                  throw new Error(
                    `HTTP error! status: ${response.status} (after client token refresh)`,
                  );
                }
              } else {
                // Token refresh failed, throw the original error
                throw new Error(
                  `HTTP error! status: ${response.status} (client token refresh failed)`,
                );
              }
            } else {
              // Not an "Invalid client token" error, throw the original error
              throw new Error(`HTTP error! status: ${response.status}`);
            }
          } catch (parseError) {
            // If we can't parse the response, throw the original error
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        } else if (!response.ok) {
          // Record failure for any HTTP error
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();


        // Cache successful response (only if cacheKey is provided and not skipping cache)
        if (cacheKey && !skipCache) {
          cacheService.set(cacheKey, data, cacheTTL);
        }
        return data;
      } catch (error) {
        // Handle network errors (like "Failed to fetch") - these are different from HTTP errors
        // Network errors typically mean the request couldn't reach the server
        if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
          const isLocalhost = window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1' ||
                             window.location.hostname.includes('localhost');
          
          // Check if this might be a CORS issue (common with CloudFront/CDN)
          const isCDNURL = this.baseURL.includes('cloudfront.net') || 
                          this.baseURL.includes('cdn') ||
                          path.includes('cloudfront');
          
          console.error('🌐 SpotlightService: Network error (Failed to fetch):', {
            path,
            error: error.message,
            baseURL: this.baseURL,
            isLocalhost,
            isCDNURL,
            fullURL: `${this.baseURL}${path}`,
          });
          
          // Provide more specific error messages based on context
          let errorMessage;
          if (isCDNURL) {
            errorMessage = `CORS or network error: Unable to reach CDN at ${this.baseURL}${path}. This may be a CORS configuration issue or the CDN may be blocking the request.`;
          } else if (isLocalhost) {
            errorMessage = `Network error: Unable to reach server at ${this.baseURL}${path}. This may be a CORS issue, SSL certificate problem, or the server may not be running. Check your development server configuration.`;
          } else {
            errorMessage = `Network error: Unable to reach server at ${this.baseURL}${path}. Please check your connection and try again.`;
          }
          
          throw new Error(errorMessage);
        }
        
        // Record failure for any other caught error
        throw error;
      } finally {
        // Clean up pending request (only if cacheKey is provided)
        if (cacheKey) {
          this.pendingRequests.delete(cacheKey);
        }
      }
    })();

    // Track the pending request before any await operations (only if cacheKey is provided)
    if (cacheKey) {
      this.pendingRequests.set(cacheKey, requestPromise);
    }
    return requestPromise;
  }

  /**
   * Get AppLoad configuration data
   * @returns {Promise<AppLoadResponse>} AppLoad configuration containing homepage data, app configuration, and user data
   */
  /**
   * Development-only auth refresh call
   * @returns {Promise<void>}
   */
  async devRefreshAuth() {
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    try {
      const result = await this.callAPI('auth/refresh', 'DEV_AUTH_REFRESH', {
        method: 'POST',
        cacheTTL: 30 * 60 * 1000, // 30 minutes cache
        body: JSON.stringify({
          token: window.EMBEDDED_JWT,
        }),
        skipCache: true,
      });

      // Update the embedded JWT with the new token if provided
      if (result.success && result.token) {
        window.EMBEDDED_JWT = result.token;

        // Also update the appload cache with the new token
        const appLoadData = cacheService.get(appLoadCacheKey);
        if (appLoadData && appLoadData.app_data) {
          appLoadData.app_data.token = result.token;
          cacheService.set(appLoadCacheKey, appLoadData, appLoadData.ttl || 1440);
        }
      }
    } catch (error) {
      console.warn('🔧 Development mode: Auth refresh error:', error);
    }
  }

  async getAppLoad(forceRefresh = false) {
    const appLoadUrl = `appload/website/US?include_video_id=true&include_child_info=true&include_subscription_access=true`;
    
    // Debug: Check if we have a client token before making the call
    const clientToken = await this.getClientToken();
    console.log('🔄 getAppLoad: Has client token?', !!clientToken);
    console.log('🔄 getAppLoad: Client token length:', clientToken?.length);
    console.log('🔄 getAppLoad: Client token preview:', clientToken?.substring(0, 50) + '...');
    console.log('🔄 getAppLoad: forceRefresh:', forceRefresh);
    
    // If we don't have a client token and user is authenticated, wait a bit and try again
    if (!clientToken && this.currentUser) {
      console.warn('⚠️ getAppLoad: No client token but user exists, waiting 500ms and retrying...');
      await new Promise(resolve => setTimeout(resolve, 500));
      const retryToken = await this.getClientToken();
      console.log('🔄 getAppLoad: Retry - Has client token?', !!retryToken);
      console.log('🔄 getAppLoad: Retry - Client token length:', retryToken?.length);
    }
    
    const response = await this.callAPI(appLoadUrl, appLoadCacheKey, {
      method: 'POST',
      skipCache: forceRefresh, // Skip cache if force refresh is requested
      requiresClientAuth: true, // Include client token to get subscription status
    });

    // Appload response received

    // Update the primary access token cache with the token from appload if available
    if (response.app_data?.token) {
      tokenService.storeToken(response.app_data.token);
      // Appload token stored in primary cache
    }

    return response;
  }

  /**
   * Get user's watchlist data
   * @param {WatchlistOptions} options - Query options
   * @returns {Promise<WatchlistResponse>} Watchlist data with channels array
   */
  async getWatchlist(options = {}) {
    // Get current profile ID for cache key
    const currentProfile = profileService.getCurrentProfile();
    const profileId = currentProfile?._id || 'no-profile';
    const cacheKey = `WATCHLIST_${profileId}_${JSON.stringify(options)}`;

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (options.exclude_live) queryParams.append('exclude_live', options.exclude_live);
    if (options.exclude_vod) queryParams.append('exclude_vod', options.exclude_vod);

    const url = `watchlist/channels${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    return this.callAPI(url, cacheKey, {
      method: 'GET',
      requiresClientAuth: true,
      cacheTTL: 2 * 60 * 1000, // 2 minutes cache for watchlist data
    });
  }

  /**
   * Add channel to watchlist
   * @param {string} channelId - Channel ID to add
   * @param {string} parentChannelId - Parent channel ID (optional)
   * @returns {Promise<WatchlistAddResponse>} Success response
   */
  async addToWatchlist(channelId, parentChannelId = null) {
    // Build request body
    const requestBody = {};

    if (channelId) {
      requestBody.channel_id = channelId;
    }

    if (parentChannelId) {
      requestBody.parent_channel_id = parentChannelId;
    }

    // Get current profile ID for cache clearing
    const currentProfile = profileService.getCurrentProfile();
    const profileId = currentProfile?._id || 'no-profile';

    // Clear profile-specific watchlist cache since we're modifying it
    this.clearCache(`WATCHLIST_${profileId}_*`);

    const watchListKey = `WATCHLIST_ADD_${profileId}_${channelId}_${parentChannelId || 'none'}`;

    const result = await this.callAPI('watchlist/channels/add', watchListKey, {
      method: 'POST',
      requiresClientAuth: true,
      skipCache: true, // Don't cache POST requests
      body: JSON.stringify(requestBody),
    });

    // Update cache with the response data if successful
    if (result.success && result.result?.channels) {
      this.updateWatchlistCache(result.result.channels);
    }

    return result;
  }

  /**
   * Remove channel from watchlist
   * @param {string} channelId - Channel ID to remove
   * @returns {Promise<WatchlistDeleteResponse>} Success response
   */
  async removeFromWatchlist(channelId) {
    // Build request body
    const requestBody = {
      channel_id: channelId,
    };

    // Get current profile ID for cache clearing
    const currentProfile = profileService.getCurrentProfile();
    const profileId = currentProfile?._id || 'no-profile';

    // Clear profile-specific watchlist cache since we're modifying it
    this.clearCache(`WATCHLIST_${profileId}_*`);

    const watchListKey = `WATCHLIST_REMOVE_${profileId}_${channelId}`;

    const result = await this.callAPI('watchlist/channels/delete', watchListKey, {
      method: 'DELETE',
      requiresClientAuth: true,
      skipCache: true, // Don't cache DELETE requests
      body: JSON.stringify(requestBody),
    });

    // Update cache with the response data if successful
    if (result.success && result.result?.channels) {
      this.updateWatchlistCache(result.result.channels);
    }

    return result;
  }

  /**
   * Update watchlist cache with new data (called after successful add/remove operations)
   * @param {Array<WatchlistChannel>} channels - Updated watchlist channels
   */
  updateWatchlistCache(channels) {
    try {
      // Get current profile ID for cache key
      const currentProfile = profileService.getCurrentProfile();
      const profileId = currentProfile?._id || 'no-profile';

      // Update the profile-specific watchlist cache with the new data
      const cacheKey = `WATCHLIST_${profileId}_`;
      const cacheData = {
        channels: channels,
        success: true,
        data: {
          channels: channels,
        },
      };

      // Store in cache for 2 minutes (same TTL as getWatchlist)
      cacheService.set(cacheKey, cacheData, 2 * 60 * 1000);
    } catch (error) {
      console.error('SpotlightService: Error updating watchlist cache:', error);
    }
  }

  /**
   * Get user resumptions data (continue watching and watch again)
   * @param {ResumptionsOptions} options - Query options
   * @returns {Promise<ResumptionsResponse>} Resumptions data with continue-watching and watch-again arrays
   */
  async getResumptions(options = {}) {
    // Get current profile ID for cache key
    const currentProfile = profileService.getCurrentProfile();
    const profileId = currentProfile?._id || 'no-profile';
    const cacheKey = `RESUMPTIONS_${profileId}_${JSON.stringify(options)}`;

    // Build query parameters
    const queryParams = new URLSearchParams();

    if (options.limit) queryParams.append('limit', options.limit);
    if (options.ct_min) queryParams.append('ct_min', options.ct_min);
    if (options.ct_max) queryParams.append('ct_max', options.ct_max);
    if (options.wa_min) queryParams.append('wa_min', options.wa_min);
    if (options.most_recent !== undefined) queryParams.append('most_recent', options.most_recent);
    if (options.has_channel !== undefined)
      queryParams.append('has_channel', (options.has_channel && 1) || 0);

    const url = `users/resumption/videos${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    return this.callAPI(url, cacheKey, {
      method: 'GET',
      requiresClientAuth: true,
      cacheTTL: 2 * 60 * 1000, // 2 minutes cache for resumptions data
    });
  }

  /**
   * Get watch point for a specific video
   * @param {string} videoId - The ID of the video to get watch point for
   * @returns {Promise<WatchPointResponse>} Object containing point (seconds) and video_id, or null if no data
   */
  async getVideoWatchPoint(videoId) {
    // Get current profile ID for cache key
    const currentProfile = profileService.getCurrentProfile();
    const profileId = currentProfile?._id || 'no-profile';
    const cacheKey = `VIDEO_WATCH_POINT_READ_${profileId}_${videoId}`;

    const url = `users/videos/point/${videoId}`;

    const response = await this.callAPI(url, cacheKey, {
      method: 'GET',
      requiresClientAuth: true,
      cacheTTL: 5 * 60 * 1000, // 5 minutes cache
    });

    return response;
  }

  /**
   * Save watch point for a specific video
   * @param {string} videoId - The ID of the video to save watch point for
   * @param {number} point - Time in seconds the user has watched the video to
   * @param {WatchPointSaveOptions} options - Optional parameters
   * @returns {Promise<WatchPointSaveResponse>} Response with saved point data including progress percentage
   */
  async saveVideoWatchPoint(videoId, point, options = {}) {
    // Validate videoId
    if (!videoId) {
      console.error(
        '🎬 SpotlightService: saveVideoWatchPoint called with invalid videoId:',
        videoId,
      );
      return;
    }

    // Build query parameters for channel information
    const queryParams = new URLSearchParams();
    if (options.channelId) queryParams.append('channel_id', options.channelId);
    if (options.parentChannelId) queryParams.append('parent_channel_id', options.parentChannelId);

    const url = `users/videos/point/${videoId}/${point}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    // Get current profile ID for cache key
    const currentProfile = profileService.getCurrentProfile();
    const profileId = currentProfile?._id || 'no-profile';
    const cacheKey = `VIDEO_WATCH_POINT_SAVE_${profileId}_${videoId}`;

    // Clear the watch point cache for this video since we're updating it
    this.clearCache(`VIDEO_WATCH_POINT_READ_${profileId}_${videoId}`);

    return this.callAPI(url, cacheKey, {
      method: 'POST',
      requiresClientAuth: true,
      skipCache: true,
    });
  }

  /**
   * Clear resumptions cache to force fresh data fetch
   * This should be called when a user leaves a watch page to ensure
   * continue-watching data is up to date
   */
  clearResumptionsCache() {
    this.clearCache('RESUMPTIONS_*');
  }

  /**
   * Clear watchlist cache to force fresh data fetch
   * This should be called when profiles change to ensure
   * watchlist data is up to date
   */
  clearWatchlistCache() {
    this.clearCache('WATCHLIST_*');
  }

  /**
   * Clear video watch point cache to force fresh data fetch
   * This should be called when a user leaves a watch page to ensure
   * watch point data is up to date
   */
  clearVideoWatchPointCache() {
    this.clearCache('VIDEO_WATCH_POINT_*');
  }

  /**
   * Clear subscription-related caches to force fresh data fetch
   * This should be called when:
   * - A new client token is set (new entitlements)
   * - After a successful purchase
   * - On login/logout
   */
  clearSubscriptionCaches() {
    console.log('🗑️ SpotlightService: Clearing subscription caches...');
    
    // Clear subscription check caches
    this.clearCache('SUBSCRIPTION_CHECK_*');
    
    // Clear app load data (contains subscription_access)
    this.clearCache('SPOTLIGHT_GET_APP_LOAD');
    this.clearCache('APP_LOAD_DATA');
    
    // Clear channel caches (may contain subscription_access)
    this.clearCache('CHANNEL_*');
    
    // Also clear from localStorage directly for any legacy cache keys
    const keysToCheck = Object.keys(localStorage);
    const clearedKeys = [];
    keysToCheck.forEach(key => {
      if (
        key.includes('SUBSCRIPTION_CHECK') ||
        key.includes('APP_LOAD') ||
        (key.includes('CHANNEL') && key.includes('cache'))
      ) {
        localStorage.removeItem(key);
        clearedKeys.push(key);
      }
    });
    
    console.log('🗑️ SpotlightService: Cleared subscription caches:', clearedKeys.length, 'entries');
  }

  /**
   * Get EPG data for the specified time range
   * @param {Object} options
   * @returns {Promise<EPGDataResponse>} EPG data containing channels with their program schedules
   */
  async getEPGData(options = {}) {
    // Calculate start time: 30 minutes before now, rounded down to nearest 30 minutes
    const now = new Date();
    const startTime = new Date(now.getTime() - 30 * 60 * 1000); // 30 minutes before now

    // Round down to nearest 30 minutes (1800 seconds)
    const minutes = startTime.getMinutes();
    const roundedMinutes = Math.floor(minutes / 30) * 30;
    startTime.setMinutes(roundedMinutes, 0, 0);

    // Calculate end time: 12 hours from now
    const endTime = new Date(startTime);
    endTime.setHours(endTime.getHours() + 12);
    endTime.setMinutes(Math.ceil(endTime.getMinutes() / 30) * 30, 0, 0); // Round up to nearest 30 minutes

    const queryParams = new URLSearchParams({
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
    }).toString();

    const url = `live/epg/US?${queryParams}`;

    // Use rounded time for cache key to ensure stability
    const roundedTimestamp = Math.floor(startTime.getTime() / (30 * 60 * 1000)) * (30 * 60 * 1000);
    const cacheKey = `EPG_DATA_${roundedTimestamp}`;

    // Use a shorter TTL for EPG data since it's time-sensitive
    return this.callAPI(url, cacheKey, {
      cacheTTL: 2 * 60 * 1000, // 2 minutes TTL for EPG data
    });
  }

  /**
   * Get channel details by slug
   * @param {string} slug - Channel slug
   * @returns {Promise<ChannelBySlugResponse>} Promise that resolves to Channel data (either single channel or parent channel with children)
   */
  async getChannelBySlug(slug) {
    const countryCode = 'US';
    const channelUrl = `channel/${countryCode}/${slug}?include_subscription_access=true`;
    const cacheKey = `SPOTLIGHT_GET_CHANNEL_BY_SLUG_${countryCode}_${slug}`;
    return this.callAPI(channelUrl, cacheKey, {
      requiresClientAuth: true, // Include client token to get subscription status
    });
  }

  /**
   * Get channel by slug without requiring client auth (for PDP deep links)
   * @param {string} slug - Channel slug
   * @returns {Promise<Object>} Promise that resolves to Channel data
   */
  async getChannelBySlugPublic(slug) {
    const countryCode = 'US';
    const channelUrl = `channel/${countryCode}/${slug}`;
    const cacheKey = `SPOTLIGHT_GET_CHANNEL_BY_SLUG_PUBLIC_${countryCode}_${slug}`;
    return this.callAPI(channelUrl, cacheKey);
  }

  /**
   * Get child channel (season) details including episode-level data
   * @param {string} parentSlug - Parent channel (series) slug
   * @param {string} childSlug - Child channel (season) slug or ID
   * @returns {Promise<Object>} Promise that resolves to child channel data with episodes
   */
  async getChildChannelDetails(parentSlug, childSlug) {
    const countryCode = 'US';
    const channelUrl = `channel/${countryCode}/${parentSlug}/${childSlug}`;
    const cacheKey = `SPOTLIGHT_GET_CHILD_CHANNEL_${countryCode}_${parentSlug}_${childSlug}`;
    
    const response = await this.callAPI(channelUrl, cacheKey, {
      requiresClientAuth: true, // Include client token to get subscription status
    });
    
    // The API returns the child channel data directly
    // Episodes with bypass_channel_lock flag will be in the playlist
    return response;
  }

  /**
   * Get channel details by ID
   * @param {string} channelId - Channel ID (MongoDB ObjectId)
   * @returns {Promise<Object>} Promise that resolves to Channel data
   */
  async getChannelById(channelId) {
    const countryCode = 'US';
    const channelUrl = `channel/${countryCode}/id/${channelId}`;
    const cacheKey = `SPOTLIGHT_GET_CHANNEL_BY_ID_${countryCode}_${channelId}`;

    const response = await this.callAPI(channelUrl, cacheKey);

    // Handle different response structures
    if (!response) {
      throw new Error('No response from API.');
    }

    if (!response.success) {
      throw new Error('Could not get channel details from id');
    }

    if (response.data?._id) {
      return response.data;
    }

    console.error('🚨 SpotlightService.getChannelById: Unexpected response structure:', response);
    throw new Error('Unexpected API response structure.');
  }

  /**
   * Get related channels by slug
   * @param {string} slug - Channel slug
   * @returns {Promise<RelatedChannelsResponse>} Promise that resolves to related Channel data with pagination
   */
  async getRelatedChannelsByChannelSlug(slug) {
    const relatedUrl = `find/recommendations/${slug}/website?include_child_info=true`;
    const cacheKey = `SPOTLIGHT_GET_RELATED_CHANNELS_BY_SLUG_${slug}`;
    return this.callAPI(relatedUrl, cacheKey);
  }

  async checkHomepageCacheForCategory(slug) {
    const homepage = await this.getAppLoad().catch(e => {
      throw new Error(e);
    });
    if (homepage?.length) {
      const hpData = homepage.find(hp => hp.category.slug === slug);
      if (!hpData) return null;
      return Object.assign(hpData.category, { channels: hpData.channels });
    }
    return null;
  }

  /**
   * Get channel details by slug
   * @param {string} slug - Channel slug
   * @returns {Promise<PageCategoriesResponse>} Promise that resolves to movies page categories
   */
  async getMoviesPageCategories() {
    const channelUrl = `categories/US?movies_page=true`;
    const cacheKey = `SPOTLIGHT_GET_MOVIES_CATEGORIES`;
    return this.callAPI(channelUrl, cacheKey);
  }

  /**
   * Get channel details by slug
   * @param {string} slug - Channel slug
   * @returns {Promise<PageCategoriesResponse>} Promise that resolves to series page categories
   */
  async getSeriesPageCategories() {
    const channelUrl = `categories/US?series_page=true`;
    const cacheKey = `SPOTLIGHT_GET_SERIES_CATEGORIES`;
    return this.callAPI(channelUrl, cacheKey);
  }

  /**
   * Get movies homepage data
   * @returns {Promise<{movies: HomepageItem[]}>} Promise that resolves to movies page categories
   */
  async getMoviesHomePage() {
    const channelUrl = `movies/US/website?include_child_info=true&include_subscription_access=true`;
    const cacheKey = `SPOTLIGHT_GET_MOVIES_HOMEPAGE`;
    return this.callAPI(channelUrl, cacheKey, {
      method: 'GET',
      requiresClientAuth: true, // Include client token to get subscription status
    });
  }

  /**
   * Get series homepage data
   * @returns {Promise<{series: HomepageItem[]}>} Promise that resolves to series page categories
   */
  async getSeriesHomePage() {
    const channelUrl = `series/US/website?include_child_info=true&include_subscription_access=true`;
    const cacheKey = `SPOTLIGHT_GET_SERIES_HOMEPAGE`;
    return this.callAPI(channelUrl, cacheKey, {
      method: 'GET',
      requiresClientAuth: true, // Include client token to get subscription status
    });
  }

  /**
   * Get static page content by slug
   * @param {string} slug - Page slug (e.g., 'terms-of-use', 'privacy-policy', 'about', 'contact')
   * @returns {Promise<{success: boolean, page: Object}>} Promise that resolves to page data
   */
  async getPageContent(slug) {
    const pageUrl = `pages/${slug}`;
    const cacheKey = `SPOTLIGHT_GET_PAGE_${slug.toUpperCase().replace(/-/g, '_')}`;
    return this.callAPI(pageUrl, cacheKey, {
      method: 'GET',
      cacheTTL: 60, // Cache for 60 minutes
    });
  }

  /**
   * Get channel details by slug
   * @param {string} slug - Category slug
   * @returns {Promise<CategoryChannelsResponse>} Promise that resolves to Category data with channels
   */
  async getCategoryChannelsBySlug(slug) {
    const hpCachedCategory = await this.checkHomepageCacheForCategory(slug).catch(console.warn);
    if (hpCachedCategory) return hpCachedCategory;
    const channelUrl = `channels/US/${slug}`;
    const cacheKey = `SPOTLIGHT_GET_CATEGORY_CHANNELS_BY_SLUG_${slug}`;
    return this.callAPI(channelUrl, cacheKey);
  }

  /**
   * Clear cache entries by key or wildcard pattern
   * @param {string} slug - Cache key or wildcard pattern (e.g., 'WATCHLIST_*')
   */
  clearCache(slug) {
    if (!slug || typeof slug !== 'string') {
      return 0;
    }

    let clearedCount = 0;

    if (slug.includes('*')) {
      // Wildcard: clear all cache keys that start with the prefix
      const prefix = slug.split('*')[0];
      const allKeys = cacheService.getAllKeys();

      allKeys.forEach(key => {
        if (key.startsWith(prefix)) {
          cacheService.clearKey(key);
          clearedCount++;
        }
      });
    } else {
      // Single key: clear specific cache key
      cacheService.clearKey(slug);
      clearedCount = 1;
    }

    return clearedCount;
  }

  /**
   * Check if data is cached
   * @param {string} slug - Cache key
   * @param {string} type - Cache type
   * @returns {boolean} Whether data is cached
   */
  isCached(slug, type) {
    const cacheKey = `${type}_${slug}`;
    return !!cacheService.get(cacheKey);
  }

  /**
   * Get current in-flight requests (for debugging)
   * @returns {Array} - Array of cache keys for in-flight requests
   */
  getInflightRequests() {
    return Array.from(this.pendingRequests.keys());
  }

  // ============================================================================
  // PROFILE MANAGEMENT METHODS
  // ============================================================================

  /**
   * Get all available profiles for the current user
   * @returns {Promise<ProfilesResponse>} Promise that resolves to profiles data
   */
  async getProfiles() {
    const url = 'users/profiles';
    const cacheKey = getProfileCacheKey;

    // Don't cache profiles as they can change frequently
    return this.callAPI(url, cacheKey, {
      requiresClientAuth: true,
      cacheTTL: 0, // No caching for profiles
    });
  }

  /**
   * Create a new user profile
   * @param {string} profileName - Name for the new profile
   * @returns {Promise<ProfileOperationResponse>} Promise that resolves to operation result
   */
  async createProfile(profileName) {
    const url = 'users/profiles/create';
    const cacheKey = 'SPOTLIGHT_CREATE_PROFILE';

    const requestBody = {
      profile_name: profileName,
      generate_avatar: true, // Request avatar generation for new profile
    };

    // Clear profiles cache after creating a new profile
    const result = await this.callAPI(url, cacheKey, {
      method: 'POST',
      requiresClientAuth: true,
      body: JSON.stringify(requestBody),
      cacheTTL: 0, // No caching for profile operations
      skipCache: true,
    });

    // Clear profiles cache to ensure fresh data
    this.clearCache(getProfileCacheKey);

    return result;
  }

  /**
   * Update an existing profile's name
   * @param {string} profileId - Profile ID to update
   * @param {string} profileName - New name for the profile
   * @returns {Promise<ProfileOperationResponse>} Promise that resolves to operation result
   */
  async updateProfile(profileId, profileName) {
    const url = `users/profile/${profileId}/update`;
    const cacheKey = `SPOTLIGHT_UPDATE_PROFILE_${profileId}`;

    const requestBody = {
      profile_name: profileName,
    };

    // Clear profiles cache after updating a profile
    const result = await this.callAPI(url, cacheKey, {
      method: 'PUT',
      requiresClientAuth: true,
      body: JSON.stringify(requestBody),
      cacheTTL: 0, // No caching for profile operations
      skipCache: true,
    });

    // Clear profiles cache to ensure fresh data
    this.clearCache(getProfileCacheKey);

    return result;
  }

  /**
   * Regenerate the avatar for a user profile
   * @param {string} profileId - Profile ID to regenerate avatar for
   * @returns {Promise<ProfileOperationResponse>} Promise that resolves to operation result with new avatar URL
   */
  async regenerateProfileAvatar(profileId) {
    const url = `users/profile/${profileId}/regenerate`;
    const cacheKey = `SPOTLIGHT_REGENERATE_PROFILE_AVATAR_${profileId}`;

    const result = await this.callAPI(url, cacheKey, {
      method: 'POST',
      cacheTTL: 0, // No caching for profile operations
      requiresClientAuth: true,
      skipCache: true,
    });

    // Clear profiles cache to ensure fresh data
    this.clearCache(getProfileCacheKey);

    return result;
  }

  /**
   * Delete a user profile
   * @param {string} profileId - Profile ID to delete
   * @returns {Promise<ProfileOperationResponse>} Promise that resolves to operation result
   */
  async deleteProfile(profileId) {
    const url = `users/profile/${profileId}/delete`;
    const cacheKey = `SPOTLIGHT_DELETE_PROFILE_${profileId}`;

    // Clear profiles cache after deleting a profile
    const result = await this.callAPI(url, cacheKey, {
      method: 'DELETE',
      requiresClientAuth: true,
      cacheTTL: 0, // No caching for profile operations
      skipCache: true,
    });

    // Clear profiles cache to ensure fresh data
    this.clearCache(getProfileCacheKey);

    return result;
  }

  /**
   * Get a profile token for API requests on behalf of a specific profile
   * @param {string} profileId - Profile ID to get token for
   * @returns {Promise<ProfileTokenResponse>} Promise that resolves to profile token
   */
  async getProfileToken(profileId) {
    if (!profileId || profileId === 'undefined') {
      console.warn('SpotlightService: getProfileToken called with invalid profileId:', profileId);
      return { success: false, error: 'Invalid profile ID' };
    }
    
    const url = `users/profile/${profileId}/auth`;
    const cacheKey = `SPOTLIGHT_GET_PROFILE_TOKEN_${profileId}`;

    // Clear any cached response for this profile token
    cacheService.invalidate(cacheKey);

    // Also check if there's a critical cache entry that needs to be cleared
    const criticalCacheKey = `SPOTLIGHT_GET_PROFILE_TOKEN_${profileId}`;
    const criticalCachedData = cacheService.getCritical(criticalCacheKey);
    if (criticalCachedData) {
      cacheService.removeCriticalDataFromStorage(criticalCacheKey);
    }

    // Don't cache profile tokens as they should be fresh each time
    const result = await this.callAPI(url, cacheKey, {
      cacheTTL: 0, // No caching for profile tokens
      requiresClientAuth: true,
      skipCache: true, // Force skip cache to ensure fresh API call
    });

    return result;
  }

  /**
   * Set the current profile token for subsequent API calls
   * @param {string} profileId - Profile ID
   * @param {string} profileToken - Profile token to use for API calls
   */
  setCurrentProfileToken(profileId, profileToken) {
    if (profileId && profileToken) {
      cacheService.setProfileToken(profileId, profileToken);
    }
  }

  /**
   * Get the current profile token
   * @returns {string|null} Current profile token or null if not set
   */
  getCurrentProfileToken() {
    const currentProfile = profileService.getCurrentProfile();
    if (currentProfile?._id) {
      return cacheService.getProfileToken(currentProfile._id);
    }
    return null;
  }

  /**
   * Clear the current profile token
   */
  clearCurrentProfileToken() {
    const currentProfile = profileService.getCurrentProfile();
    if (currentProfile?._id) {
      cacheService.clearProfileToken(currentProfile._id);
    }
  }

  /**
   * Check if a profile token is currently set
   * @returns {boolean} Whether a profile token is set
   */
  hasCurrentProfileToken() {
    const currentProfile = profileService.getCurrentProfile();
    if (currentProfile?._id) {
      const token = cacheService.getProfileToken(currentProfile._id);
      const isExpired = cacheService.isProfileTokenExpired(currentProfile._id);
      return !!token && !isExpired;
    }
    return false;
  }

  /**
   * Get profile token for a specific profile, with caching and refresh logic
   * @param {string} profileId - Profile ID to get token for
   * @returns {Promise<string|null>} Profile token or null if not available
   */
  async getProfileTokenWithCache(profileId) {
    if (!profileId) {
      console.warn(
        'SpotlightService: getProfileTokenWithCache called with invalid profileId:',
        profileId,
      );
      return null;
    }

    // Check if we have a valid cached token
    const cachedToken = cacheService.getProfileToken(profileId);
    const isExpired = cacheService.isProfileTokenExpired(profileId);

    // If we have a token but it's expired, we need to refresh it
    if (cachedToken && isExpired) {
      // Token is expired, will refresh
    } else if (cachedToken && !isExpired) {
      return cachedToken;
    }

    // Get a fresh profile token from the API
    // callAPI will handle request deduplication automatically via its cache key
    try {
      const response = await this.getProfileToken(profileId);

      if (response.success && response.token) {
        // Store the token in cache with expiration
        cacheService.setProfileToken(profileId, response.token);
        return response.token;
      }

      console.warn(
        `SpotlightService: Failed to get profile token for profile ${profileId}:`,
        response,
      );
      return null;
    } catch (error) {
      console.error(
        `SpotlightService: Error getting profile token for profile ${profileId}:`,
        error,
      );
      return null;
    }
  }

  /**
   * Refresh profile token for a specific profile
   * @param {string} profileId - Profile ID to refresh token for
   * @returns {Promise<string|null>} Refreshed profile token or null if not available
   */
  async refreshProfileToken(profileId) {
    if (!profileId) {
      console.warn(
        'SpotlightService: refreshProfileToken called with invalid profileId:',
        profileId,
      );
      return null;
    }

    try {
      // Get current token for comparison
      const currentToken = cacheService.getProfileToken(profileId);

      // Get a fresh profile token from the API
      const response = await this.getProfileToken(profileId);

      if (response.success && response.token) {
        // Store the token in cache with expiration
        cacheService.setProfileToken(profileId, response.token);
        return response.token;
      }

      console.warn(
        `SpotlightService: Failed to refresh profile token for profile ${profileId}:`,
        response,
      );
      return null;
    } catch (error) {
      console.error(
        `SpotlightService: Error refreshing profile token for profile ${profileId}:`,
        error,
      );
      return null;
    }
  }

  /**
   * Set profile token in cache
   * @param {string} profileId - Profile ID
   * @param {string} profileToken - Profile token
   */
  setProfileToken(profileId, profileToken) {
    if (profileToken) {
      cacheService.setProfileToken(profileId, profileToken);
    }
  }

  /**
   * Clear profile token from cache
   * @param {string} profileId - Profile ID
   */
  clearProfileToken(profileId) {
    cacheService.clearProfileToken(profileId);
  }

  /**
  // ==========================================
  // REACTION METHODS
  // ==========================================

  /**
   * Create or update a user's reaction to a channel
   * @param {string} channelId - Channel ID to react to
   * @param {string} action - Reaction type: 'like', 'dislike', 'double_like', 'double_dislike'
   * @returns {Promise<Object>} API response
   */
  async createOrUpdateUserReaction(channelId, action) {
    if (!channelId || !action) {
      throw new Error('Channel ID and action are required');
    }

    const validActions = ['like', 'dislike', 'double_like', 'double_dislike'];
    if (!validActions.includes(action)) {
      throw new Error(`Invalid action. Must be one of: ${validActions.join(', ')}`);
    }

    console.log('👍 SpotlightService: Creating/updating reaction:', { channelId, action });

    // Build request body
    const requestBody = {
      action: action,
    };

    const currentProfile = profileService.getCurrentProfile();
    const profileId = currentProfile?._id || 'no-profile';

    const reactionKey = `REACTION_CREATE_${profileId}_${channelId}_${action}`;

    console.log('👍 SpotlightService: Making POST request to users/likes/' + channelId, requestBody);

    try {
      const result = await this.callAPI(`users/likes/${channelId}`, reactionKey, {
        method: 'POST',
        body: JSON.stringify(requestBody),
        requiresClientAuth: true,
      });

      console.log('👍 SpotlightService: Create/update reaction SUCCESS:', result);
      return result;
    } catch (error) {
      console.error('👍 SpotlightService: Create/update reaction FAILED:', error);
      console.error('👍 SpotlightService: Error details:', {
        message: error.message,
        status: error.status,
        channelId,
        action,
        endpoint: `users/likes/${channelId}`
      });
      throw error;
    }
  }

  /**
   * Get a user's reaction for a specific channel
   * @param {string} channelId - Channel ID to get reaction for
   * @returns {Promise<Object>} API response with user's reaction
   */
  async getUserChannelReaction(channelId) {
    if (!channelId) {
      throw new Error('Channel ID is required');
    }

    // Get current profile ID for cache key
    const currentProfile = profileService.getCurrentProfile();
    const profileId = currentProfile?._id || 'no-profile';

    const reactionKey = `REACTION_${profileId}_${channelId}`;

    const result = await this.callAPI(`users/reaction/${channelId}`, reactionKey, {
      method: 'GET',
      requiresClientAuth: true,
    });

    return result;
  }

  /**
   * Remove a user's reaction from a specific channel
   * @param {string} channelId - Channel ID to remove reaction from
   * @returns {Promise<Object>} API response
   */
  async removeUserChannelReaction(channelId) {
    if (!channelId) {
      throw new Error('Channel ID is required');
    }

    console.log('🗑️ SpotlightService: Removing reaction for channel:', channelId);

    const currentProfile = profileService.getCurrentProfile();
    const profileId = currentProfile?._id || 'no-profile';

    const reactionKey = `REACTION_REMOVE_${profileId}_${channelId}`;

    // Use the correct endpoint from the PRD documentation
    console.log('🗑️ SpotlightService: Using DELETE /users/reactions/' + channelId);
    console.log('🗑️ SpotlightService: Full URL will be:', this.baseURL + 'users/reactions/' + channelId);

    try {
      const result = await this.callAPI(`users/reactions/${channelId}`, reactionKey, {
        method: 'DELETE',
        requiresClientAuth: true,
      });

      console.log('🗑️ SpotlightService: Remove reaction SUCCESS:', result);
      return result;
    } catch (error) {
      console.error('🗑️ SpotlightService: Remove reaction FAILED:', error);
      console.error('🗑️ SpotlightService: Error details:', {
        message: error.message,
        status: error.status,
        channelId,
        endpoint: `users/reactions/${channelId}`
      });
      throw error;
    }
  }

  /**
   * Get user's reactions summary across all channels
   * @param {boolean} detailed - Whether to get detailed reactions or just summary
   * @returns {Promise<Object>} API response with reactions summary or detailed list
   */
  async getUserReactionsSummary(detailed = false) {
    // Get current profile ID for cache key
    const currentProfile = profileService.getCurrentProfile();
    const profileId = currentProfile?._id || 'no-profile';

    const summaryKey = detailed ? `REACTIONS_DETAILED_${profileId}` : `REACTIONS_SUMMARY_${profileId}`;
    
    // Try different endpoint variations for detailed reactions
    let endpoint = 'users/reactions';
    if (detailed) {
      // Try common API patterns for detailed data
      endpoint = 'users/reactions?detailed=true';
    }

    const result = await this.callAPI(endpoint, summaryKey, {
      method: 'GET',
      requiresClientAuth: true,
    });

    return result;
  }

  /**
   * Sync user reactions from API to localStorage
   * This should be called on app load to ensure reactions are available offline
   * @returns {Promise<boolean>} Success status
   */
  async syncUserReactionsToLocalStorage() {
    try {
      const currentProfile = profileService.getCurrentProfile();
      if (!currentProfile?._id) {
        console.log('SpotlightService: No profile available for reaction sync');
        return false;
      }

      console.log('SpotlightService: Syncing user reactions from API to localStorage...');

      // Use the correct undocumented API endpoint
      const endpoint = 'users/reactions/by-channel';
      let reactions = [];

      try {
        console.log(`SpotlightService: Making authenticated request to ${endpoint} for profile:`, currentProfile.profile_name);
        console.log('SpotlightService: Profile ID for token lookup:', currentProfile._id);
        
        const result = await this.callAPI(endpoint, `REACTION_SYNC_${currentProfile._id}`, {
          method: 'GET',
          requiresClientAuth: true, // This ensures both client and profile tokens are sent
          cacheTTL: 0, // Don't cache sync requests
        });

        console.log('SpotlightService: Raw API result:', result);
        console.log('SpotlightService: result.actions exists:', !!result?.actions);
        console.log('SpotlightService: result.actions is array:', Array.isArray(result?.actions));
        
        if (result?.actions && Array.isArray(result.actions)) {
          reactions = result.actions;
          console.log(`SpotlightService: Successfully fetched ${reactions.length} reactions from ${endpoint}`);
          console.log('SpotlightService: reactions array after assignment:', reactions);
        } else if (result?.data && Array.isArray(result.data)) {
          reactions = result.data;
          console.log(`SpotlightService: Successfully fetched ${reactions.length} reactions from ${endpoint}`);
        } else if (result && Array.isArray(result)) {
          reactions = result;
          console.log(`SpotlightService: Successfully fetched ${reactions.length} reactions from ${endpoint}`);
        } else {
          console.log('SpotlightService: API returned data but not in expected array format:', result);
        }
      } catch (error) {
        console.warn(`SpotlightService: Failed to fetch from ${endpoint}:`, error.message);
        return false;
      }

      console.log('SpotlightService: Final reactions array before length check:', reactions);
      console.log('SpotlightService: reactions.length:', reactions.length);
      
      if (reactions.length === 0) {
        console.log('SpotlightService: No reactions found from API endpoint');
        return false;
      }

      // Sync reactions to localStorage
      let syncedCount = 0;
      for (const reaction of reactions) {
        try {
          // Handle different possible reaction data structures
          const channelId = reaction.channel_id || reaction.channelId || reaction.dspro_id || reaction._id;
          const action = reaction.reaction || reaction.action || reaction.type;

          if (channelId && action) {
            const key = `reaction_${currentProfile._id}_${channelId}`;
            localStorage.setItem(key, JSON.stringify(action));
            console.log(`SpotlightService: Stored reaction for profile ${currentProfile.profile_name}: ${key} = ${action}`);
            syncedCount++;
          }
        } catch (error) {
          console.warn('SpotlightService: Failed to sync reaction to localStorage:', error);
        }
      }

      console.log(`SpotlightService: Successfully synced ${syncedCount} reactions to localStorage`);
      return syncedCount > 0;

    } catch (error) {
      console.error('SpotlightService: Error syncing reactions to localStorage:', error);
      return false;
    }
  }


  /**
   * Get detailed user info (including reactions)
   * @returns {Promise<Object>} API response with detailed user information
   */
  async getUserInfo() {
    // Get current user from cache to get customer_id
    const currentUser = cacheService.get('USER');
    if (!currentUser) {
      throw new Error('No user found - please log in');
    }

      // Extract customer ID from user data - try different possible fields
      const customerId = currentUser['https://www.electricnow.tv/customer_id'] ||
                        currentUser['https://www.electricnow.tv/user_id'] ||
                        currentUser.customer_id ||
                        currentUser.user_id;
                        
      if (!customerId) {
        throw new Error('No customer ID found in user data');
      }

    const userInfoKey = `USER_INFO_${customerId}`;

    // Build URL with query parameter
    const urlWithParams = `users/info?customer_id=${encodeURIComponent(customerId)}`;

    const result = await this.callAPI(urlWithParams, userInfoKey, {
      method: 'POST',
      requiresClientAuth: false, // Only needs x-access-token, not client/profile tokens
      body: JSON.stringify({}), // POST requests typically need a body, even if empty
    });

    return result;
  }

  /**
   * Check if we have a valid profile token for a specific profile
   * @param {string} profileId - Profile ID to check
   * @returns {boolean} True if we have a valid token
   */
  hasValidProfileToken(profileId) {
    const cachedToken = cacheService.getProfileToken(profileId);
    const isExpired = cacheService.isProfileTokenExpired(profileId);
    return !!cachedToken && !isExpired;
  }

  /**
   * Check subscription access for a channel (TVOD/Paywall)
   * Returns unlock status and ads_enabled flag for parent and child channels
   * 
   * @param {string} channelId - Channel ID or dspro_id to check
   * @returns {Promise<Object>} Subscription access data
   * @example
   * {
   *   success: true,
   *   unlocked: true,
   *   ads_enabled: true,
   *   childchannels: [
   *     {
   *       dspro_id: "...",
   *       unlocked: true,
   *       ads_enabled: true
   *     }
   *   ]
   * }
   */
  async checkSubscriptionAccess(channelId) {
    if (!channelId) {
      throw new Error('Channel ID is required for subscription check');
    }

    console.log('💳 SpotlightService: Checking subscription access for channel:', channelId);

    const cacheKey = `SUBSCRIPTION_CHECK_${channelId}`;

    try {
      const result = await this.callAPI(
        `subscriptions/check/${channelId}`,
        cacheKey,
        {
          method: 'GET',
          requiresClientAuth: true,
          cacheTTL: 5 * 60 * 1000, // Cache for 5 minutes
        }
      );

      console.log('💳 SpotlightService: Subscription check result:', {
        channelId,
        unlocked: result?.unlocked,
        ads_enabled: result?.ads_enabled,
        purchasable: result?.purchasable,
        dspro_id: result?.dspro_id,
        hasChildChannels: !!result?.childchannels?.length
      });

      return result;
    } catch (error) {
      console.error('💳 SpotlightService: Subscription check failed:', error);
      
      // Return default locked state on error (safe default)
      return {
        success: false,
        unlocked: false,
        ads_enabled: true,
        childchannels: []
      };
    }
  }

  /**
   * Get all products a channel is part of
   * @param {string} channelId - Channel ID to get products for
   * @returns {Promise<Object>} Products data
   */
  async getProductsByChannel(channelId) {
    try {
      console.log('🛒 SpotlightService: Fetching products for channel:', channelId);
      
      const response = await this.callAPI(`subscriptions/products-by-channel/${channelId}`, null, {
        method: 'GET',
        skipCache: true, // Don't cache product calls
      });

      console.log('🛒 SpotlightService: Products response:', response);
      
      return response;
    } catch (error) {
      console.error('🛒 SpotlightService: Error fetching products:', error);
      throw error;
    }
  }

  /**
   * Get products for multiple channels in a single request
   * Useful for checkout to see all available products for a channel and its children (seasons)
   * @param {string[]} channelIds - Array of channel IDs (dspro_ids) to get products for
   * @returns {Promise<Object>} Products data for all requested channels
   */
  async getProductsByChannels(channelIds) {
    try {
      if (!channelIds || !Array.isArray(channelIds) || channelIds.length === 0) {
        console.warn('🛒 SpotlightService: getProductsByChannels called with invalid channelIds:', channelIds);
        return { success: false, products: [] };
      }

      // Filter out any undefined/null values
      const validChannelIds = channelIds.filter(id => id && id !== 'undefined');
      
      if (validChannelIds.length === 0) {
        console.warn('🛒 SpotlightService: No valid channel IDs after filtering');
        return { success: false, products: [] };
      }

      console.log('🛒 SpotlightService: Fetching products for channels:', validChannelIds);
      
      const response = await this.callAPI('subscriptions/products-by-channels', null, {
        method: 'POST',
        body: { channels: validChannelIds },
        skipCache: true,
      });

      console.log('🛒 SpotlightService: Products by channels response:', response);
      
      return response;
    } catch (error) {
      console.error('🛒 SpotlightService: Error fetching products by channels:', error);
      throw error;
    }
  }

  /**
   * Check if user has an existing payment profile
   * @returns {Promise<boolean>} True if user has a payment profile
   */
  async hasPaymentProfile() {
    try {
      console.log('💳 SpotlightService: Checking for existing payment profile...');
      
      // First, check localStorage for cached payment profile
      // This handles the case where user added payment but hasn't purchased yet
      const cachedProfile = localStorage.getItem('electricnow_payment_profile');
      if (cachedProfile) {
        try {
          JSON.parse(cachedProfile); // Validate it's valid JSON
          console.log('💳 SpotlightService: Found cached payment profile in localStorage');
          return true;
        } catch (e) {
          console.warn('💳 SpotlightService: Invalid cached profile, ignoring');
        }
      }
      
      // Get user's products - if they have any, they have a payment profile
      const products = await this.getUserPurchases();
      
      if (!products || products.length === 0) {
        console.log('💳 SpotlightService: No products found, no payment profile');
        return false;
      }
      
      // Check if any product has credit card info
      console.log('💳 SpotlightService: First product sample:', products[0]);
      const hasCard = products.some(product => product.credit_card);
      
      console.log('💳 SpotlightService: Payment profile exists:', hasCard);
      
      // If no credit card found, just check if they have ANY products (means they've purchased before)
      if (!hasCard && products.length > 0) {
        console.log('💳 SpotlightService: No credit_card field, but user has products. Assuming payment profile exists.');
        return true;
      }
      
      return hasCard;
    } catch (error) {
      console.error('💳 SpotlightService: Error checking payment profile:', error);
      // If we can't check, assume they don't have one (safer to show payment form)
      return false;
    }
  }

  /**
   * Subscribe user to a product (for existing customers with payment profile)
   * @param {string} productChargifyId - Chargify product ID
   * @param {string} channelId - Channel ID (required for individual channel products/TVOD)
   * @returns {Promise<Object>} Subscription data
   */
  async subscribeToProduct(productChargifyId, channelId = null, productType = null) {
    try {
      console.log('💳 SpotlightService: Subscribing existing customer to product:', productChargifyId);
      console.log('💳 SpotlightService: Channel ID for subscription:', channelId);
      console.log('💳 SpotlightService: Product type:', productType);
      
      // Build URL with platform as query param (required by API)
      const url = `subscriptions/users/subscribe_to/${productChargifyId}?platform=web`;
      
      // Build request body - channel_id is ONLY required for individual_cod products (TVOD)
      // For catalog/subscription products, don't send channel_id
      const requestBody = {};
      if (channelId && productType === 'individual_cod') {
        requestBody.channel_id = channelId;
        console.log('💳 SpotlightService: Including channel_id for individual_cod product');
      } else if (productType !== 'individual_cod') {
        console.log('💳 SpotlightService: Not including channel_id (product type is not individual_cod)');
      }
      
      console.log('💳 SpotlightService: Subscribe URL:', url);
      console.log('💳 SpotlightService: Request body:', JSON.stringify(requestBody));
      
      // Only send body if we have content
      const fetchOptions = {
        method: 'POST',
        requiresClientAuth: true,
        skipCache: true,
      };
      
      if (Object.keys(requestBody).length > 0) {
        fetchOptions.body = JSON.stringify(requestBody);
        fetchOptions.headers = { 'Content-Type': 'application/json' };
      }
      
      const response = await this.callAPI(url, null, fetchOptions);

      console.log('✅ Subscription successful:', response);
      return response;
    } catch (error) {
      console.error('❌ SpotlightService: Subscription failed:', error);
      
      // Check for already subscribed error
      if (error.message?.includes('422') || error.message?.includes('409')) {
        console.log('⚠️ User may already be subscribed to this product');
        return {
          success: false,
          alreadySubscribed: true,
          message: 'You already own this content'
        };
      }
      
      throw error;
    }
  }

  /**
   * Create payment profile and subscribe user (for new customers)
   * @param {Object} tokenData - Token data from Chargify.js (includes token, firstName, lastName)
   * @param {string} productChargifyId - Chargify product ID
   * @param {string} channelId - Channel ID (required for individual channel products/TVOD)
   * @param {string} productType - Product type (e.g., 'individual_cod' for TVOD)
   * @returns {Promise<Object>} Subscription data
   */
  async createPaymentAndSubscribe(tokenData, productChargifyId, channelId = null, productType = null) {
    try {
      console.log('💳 SpotlightService: Creating payment profile and subscribing...');
      console.log('💳 Token Data:', tokenData);
      console.log('💳 Product Chargify ID:', productChargifyId);
      console.log('💳 Channel ID:', channelId);
      console.log('💳 Product Type:', productType);

      // Extract token and name from Chargify response
      const chargifyToken = tokenData.token || tokenData;
      const firstName = tokenData.firstName || 'Customer';
      const lastName = tokenData.lastName || 'User';

      let paymentProfile = null;

      // Step 1: Create payment profile (if needed)
      try {
        console.log('💳 Step 1: Creating payment profile...');
        paymentProfile = await this.callAPI('subscriptions/users/create_with_payment', null, {
          method: 'POST',
          body: JSON.stringify({ 
            chargify_token: chargifyToken,
            first_name: firstName,
            last_name: lastName
          }),
          headers: { 'Content-Type': 'application/json' },
          requiresClientAuth: true,
          skipCache: true,
        });

        console.log('✅ Payment profile created:', paymentProfile);
      } catch (profileError) {
        // 422 means payment profile already exists - that's okay, continue to subscribe
        if (profileError.message && profileError.message.includes('422')) {
          console.log('ℹ️ Payment profile already exists, continuing to subscription...');
          paymentProfile = { success: true, message: 'Payment profile already exists' };
        } else {
          throw profileError;
        }
      }

      // Step 2: Subscribe to product
      try {
        console.log('💳 Step 2: Subscribing to product...');
        
        // Build URL with platform as query param (required by API)
        const subscribeUrl = `subscriptions/users/subscribe_to/${productChargifyId}?platform=web`;
        
        // Build request body - channel_id is ONLY required for individual_cod products (TVOD)
        const subscribeBody = {};
        if (channelId && productType === 'individual_cod') {
          subscribeBody.channel_id = channelId;
          console.log('💳 SpotlightService: Including channel_id for individual_cod product');
        } else if (productType !== 'individual_cod') {
          console.log('💳 SpotlightService: Not including channel_id (product type is not individual_cod)');
        }
        
        console.log('💳 SpotlightService: Subscribe URL:', subscribeUrl);
        console.log('💳 SpotlightService: Subscribe body:', JSON.stringify(subscribeBody));
        
        // Only send body if we have content
        const fetchOptions = {
          method: 'POST',
          requiresClientAuth: true,
          skipCache: true,
        };
        
        if (Object.keys(subscribeBody).length > 0) {
          fetchOptions.body = JSON.stringify(subscribeBody);
          fetchOptions.headers = { 'Content-Type': 'application/json' };
        }
        
        const subscription = await this.callAPI(
          subscribeUrl,
          null,
          fetchOptions
        );

        console.log('✅ Subscription created:', subscription);

        return {
          success: true,
          paymentProfile,
          subscription,
        };
      } catch (subscriptionError) {
        // 409 means already subscribed - treat as success
        if (subscriptionError.message && subscriptionError.message.includes('409')) {
          console.log('ℹ️ User is already subscribed to this product');
          return {
            success: true,
            alreadySubscribed: true,
            paymentProfile,
            subscription: { message: 'Already subscribed' },
          };
        } else {
          throw subscriptionError;
        }
      }
    } catch (error) {
      console.error('❌ SpotlightService: Error creating payment and subscription:', error);
      throw error;
    }
  }

  // NOTE: Duplicate subscribeToProduct removed - use the one defined above at line ~3142

  /**
   * Get user's purchased products/subscriptions
   * @returns {Promise<Array>} Array of purchased products with channel data
   */
  async getUserPurchases() {
    try {
      console.log('💳 SpotlightService: Fetching user purchases...');
      
      // Check if we have tokens
      const clientToken = this.getClientToken();
      const profileToken = profileService.getProfileToken();
      console.log('💳 SpotlightService: Has client token?', !!clientToken);
      console.log('💳 SpotlightService: Has profile token?', !!profileToken);
      
      // Try multiple endpoints in order of preference
      const endpoints = [
        'subscriptions/users/active_subscriptions', // Active subscriptions endpoint (primary)
        'subscriptions/purchases',              // Purchase history endpoint (fallback)
        'subscriptions/users/products',         // Products endpoint (may not be available)
      ];
      
      let response = null;
      let successEndpoint = null;
      
      for (const endpoint of endpoints) {
        try {
          console.log(`💳 SpotlightService: Trying endpoint: ${endpoint}`);
          response = await this.callAPI(endpoint, null, {
            method: 'GET',
            requiresClientAuth: true,
            requiresProfileAuth: true,
            skipCache: true,
          });
          successEndpoint = endpoint;
          console.log(`💳 SpotlightService: Success with endpoint: ${endpoint}`);
          break; // Exit loop on first successful response
        } catch (endpointError) {
          console.warn(`💳 SpotlightService: Endpoint ${endpoint} failed:`, endpointError.message);
          // Continue to next endpoint
        }
      }
      
      if (!response) {
        console.warn('💳 SpotlightService: All endpoints failed, returning empty array');
        return [];
      }

      console.log('💳 SpotlightService: User purchases response:', response);
      console.log('💳 SpotlightService: Response type:', typeof response);
      console.log('💳 SpotlightService: Response keys:', response ? Object.keys(response) : 'null');
      
      // Handle different response structures based on endpoint
      
      // Direct array response
      if (Array.isArray(response)) {
        return response;
      }
      
      // Handle subscriptions/purchases response: { success: true, purchases: [...] }
      if (response?.purchases && Array.isArray(response.purchases)) {
        console.log('💳 SpotlightService: Found purchases array:', response.purchases.length);
        return response.purchases;
      }
      
      // Handle subscriptions/users/active_subscriptions response: { success: true, subscriptions: [...] }
      if (response?.subscriptions && Array.isArray(response.subscriptions)) {
        console.log('💳 SpotlightService: Found subscriptions array:', response.subscriptions.length);
        return response.subscriptions;
      }
      
      // Handle the actual API structure: { success: true, products: { svod: [], tvod: [] } }
      if (response?.products && typeof response.products === 'object') {
        const allProducts = [];
        
        // Combine SVOD and TVOD products
        if (Array.isArray(response.products.svod)) {
          allProducts.push(...response.products.svod);
        }
        if (Array.isArray(response.products.tvod)) {
          allProducts.push(...response.products.tvod);
        }
        
        console.log('💳 SpotlightService: Combined products:', allProducts.length);
        return allProducts;
      }
      
      if (response?.products && Array.isArray(response.products)) {
        return response.products;
      }
      
      if (response?.data && Array.isArray(response.data)) {
        return response.data;
      }
      
      console.warn('💳 SpotlightService: Unexpected response structure, returning empty array');
      return [];
    } catch (error) {
      console.error('❌ SpotlightService: Error fetching user purchases:', error);
      return [];
    }
  }

  /**
   * Fetches the user's transaction history from the v2 API endpoint
   * Uses v2/subscriptions/users/products for detailed transaction data
   * @returns {Promise<Array>} Array of transactions with product and payment details
   */
  async getTransactionHistory() {
    try {
      console.log('📋 SpotlightService: Fetching transaction history...');
      
      // Check if we have tokens
      const clientToken = this.getClientToken();
      const profileToken = profileService.getProfileToken();
      console.log('📋 SpotlightService: Has client token?', !!clientToken);
      console.log('📋 SpotlightService: Has profile token?', !!profileToken);
      
      const response = await this.callAPI('v2/subscriptions/users/products', null, {
        method: 'GET',
        requiresClientAuth: true,
        requiresProfileAuth: true,
        skipCache: true,
      });

      console.log('📋 SpotlightService: Transaction history response:', response);
      console.log('📋 SpotlightService: Response type:', typeof response);
      console.log('📋 SpotlightService: Response keys:', response ? Object.keys(response) : 'null');
      
      // Handle the v2/subscriptions/users/products response structure:
      // { success: true, subscription: {...}|null, transactional_purchases: [...] }
      
      const result = {
        subscription: null,
        transactional_purchases: [],
      };
      
      // Extract subscription data (SVOD)
      if (response?.subscription) {
        result.subscription = response.subscription;
        console.log('📋 SpotlightService: Found active subscription');
      }
      
      // Extract transactional purchases (TVOD)
      if (response?.transactional_purchases && Array.isArray(response.transactional_purchases)) {
        result.transactional_purchases = response.transactional_purchases;
        console.log('📋 SpotlightService: Found transactional purchases:', response.transactional_purchases.length);
      }
      
      // Fallback: if response is just an array, treat as transactional purchases
      if (Array.isArray(response)) {
        result.transactional_purchases = response;
      }
      
      console.log('📋 SpotlightService: Returning transaction data:', {
        hasSubscription: !!result.subscription,
        purchaseCount: result.transactional_purchases.length
      });
      
      return result;
    } catch (error) {
      console.error('❌ SpotlightService: Error fetching transaction history:', error);
      throw error; // Re-throw so the page can handle and display the error
    }
  }

  /**
   * DEBUG UTILITY: Inject an expired token for testing token refresh
   * Usage: window.spotlightService.injectExpiredToken('your-expired-token-here')
   * @param {string} expiredToken - The expired JWT token to inject
   */
  injectExpiredToken(expiredToken) {
    console.log('🧪 DEBUG: Injecting expired token for testing...');
    console.log('🧪 Token length:', expiredToken.length);
    
    // Store the expired token using TokenService
    tokenService.storeToken(expiredToken);
    
    console.log('🧪 Expired token injected successfully!');
    console.log('🧪 Next API call requiring auth should trigger a 401/400 and attempt token refresh');
    console.log('🧪 To test: Try navigating to a page that requires authentication or reload the page');
    
    return {
      success: true,
      message: 'Expired token injected. Next authenticated API call will test refresh flow.',
      token: expiredToken.substring(0, 20) + '...' // Show first 20 chars for verification
    };
  }

  /**
   * DEBUG UTILITY: Clear all tokens and force re-authentication
   * Usage: window.spotlightService.clearAllTokens()
   */
  clearAllTokens() {
    console.log('🧪 DEBUG: Clearing all tokens...');
    
    // Clear access token
    tokenService.clearToken();
    
    // Clear client token
    localStorage.removeItem('CLIENT_TOKEN');
    localStorage.removeItem('CLIENT_TOKEN_EXPIRY');
    
    // Clear profile tokens
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('PROFILE_TOKEN_') || key.startsWith('PROFILE_TOKEN_EXPIRY_')) {
        localStorage.removeItem(key);
      }
    });
    
    console.log('🧪 All tokens cleared!');
    console.log('🧪 Reload the page to trigger fresh authentication');
    
    return {
      success: true,
      message: 'All tokens cleared. Reload to re-authenticate.'
    };
  }

}

// Export singleton instance
const spotlightService = new SpotlightService();

// Make it available on window for debugging
if (typeof window !== 'undefined') {
  window.spotlightService = spotlightService;
  console.log('🧪 DEBUG: spotlightService available on window for testing');
  console.log('🧪 Available debug methods:');
  console.log('  - window.spotlightService.injectExpiredToken(token)');
  console.log('  - window.spotlightService.clearAllTokens()');
}

export default spotlightService;
